/**
 * Copy for the bank reconciliation v2 decision queue (`/reconciliation/queue`).
 * Mounted under `reconciliation.queue` in both locale catalogs.
 */
import { AR_RECEIPT_REFUSAL_MESSAGES } from '@alinea/types';

/** AR receipt refusal copy keyed by code, so the queue's GL reasons never drift from the API's. */
const AR_RECEIPT_REFUSAL_GL_REASONS_PT_BR = Object.fromEntries(
  Object.entries(AR_RECEIPT_REFUSAL_MESSAGES).map(([code, copy]) => [code, copy.ptBr]),
) as Record<keyof typeof AR_RECEIPT_REFUSAL_MESSAGES, string>;
const AR_RECEIPT_REFUSAL_GL_REASONS_EN = Object.fromEntries(
  Object.entries(AR_RECEIPT_REFUSAL_MESSAGES).map(([code, copy]) => [code, copy.en]),
) as Record<keyof typeof AR_RECEIPT_REFUSAL_MESSAGES, string>;

export const reconciliationQueuePtBr = {
  browserTitle: 'Conciliação bancária v2',
  loading: 'Carregando a fila…',
  paymentMethods: {
    PIX: 'PIX',
    TED: 'TED',
    DOC: 'DOC',
    BOLETO: 'Boleto',
    CARD: 'Cartão',
    CARTAO: 'Cartão',
    DIRECT_DEBIT: 'Débito automático',
    DEBITO_AUTOMATICO: 'Débito automático',
    DIRECT_CREDIT: 'Crédito automático',
    TRANSFERENCIA: 'Transferência',
    TARIFA: 'Tarifa',
    SPEI: 'SPEI',
  },
  loadError: 'Não foi possível carregar a conciliação.',
  writesComingSoon: 'Disponível em breve',
  retry: 'Tentar de novo',
  header: {
    entity: 'Entidade',
    kindBank: 'Conta bancária',
    kindCard: 'Cartão',
    kindSwitch: 'Alternar entre contas bancárias e cartões',
    prevPeriod: 'Mês anterior',
    nextPeriod: 'Próximo mês',
    period: 'Período',
    periodMenu: 'Escolher mês',
    periodStatus: {
      CLOSED: 'Fechado',
      OPEN_ITEMS_one: '{{count}} em aberto',
      OPEN_ITEMS_other: '{{count}} em aberto',
      CURRENT: 'Em conciliação',
      IN_PROGRESS: 'Em andamento',
    },
    more: 'Mais ações',
    menu: {
      import: 'Importar extrato',
      acquirer: 'Relatório do adquirente',
      rules: 'Regras de conciliação',
      export: 'Exportar conciliação',
      shortcuts: 'Atalhos de teclado',
      theme: 'Tema',
    },
    /**
     * Second line of an account tab (ORCH Q18): the stale date, else the open count, else
     * "Em dia". The one banner under the figures carries the explanation; the tab only points.
     */
    accountState: {
      CLOSED: '{{month}} fechado',
      STALE_STATEMENT: 'Extrato até {{date}}',
      UP_TO_DATE: 'Em dia',
      TO_EXPLAIN_one: '{{count}} a explicar',
      TO_EXPLAIN_other: '{{count}} a explicar',
    },
    noBankAccounts: 'Nenhuma conta bancária nesta entidade.',
    noCardAccounts: 'Nenhum cartão nesta entidade.',
    comingSoon: 'Em breve',
  },
  demo: {
    title: 'Dados de demonstração.',
    body: 'Nada do que você fizer aqui é gravado — a tela está ligada a dados fictícios.',
  },
  errors: {
    RECON_V2_ACCOUNT_NOT_FOUND: 'Conta não encontrada.',
    RECON_V2_ENTITY_NOT_FOUND:
      'Entidade não encontrada. Atualize a página e selecione a entidade novamente.',
    RECON_V2_PERIOD_INVALID: 'Informe o período no formato AAAA-MM, por exemplo 2026-08.',
    RECON_V2_CARD_NOT_SUPPORTED: 'Cartões ainda não têm prova de saldo.',
    RECON_V2_CLOSE_BLOCKED: 'O período ainda tem pendências e não pode ser fechado.',
    RECON_V2_GROUP_NOT_FOUND: 'Conciliação não encontrada — talvez já tenha sido desfeita.',
    RECON_V2_IGNORE_REASON_REQUIRED: 'Informe o motivo para ignorar o movimento.',
    RECON_V2_NOT_CONFIRMABLE: 'Este movimento não está no grupo Confirmar.',
    RECON_V2_NOTHING_TO_RECONCILE: 'Selecione ao menos um documento ou classifique o valor.',
    RECON_V2_PERIOD_CLOSED: 'O período está fechado. Reabra antes de alterar conciliações.',
    RECON_V2_REOPEN_REASON_REQUIRED: 'Informe o motivo para reabrir o período.',
    RECON_V2_GL_PERIOD_CLOSED:
      'O período contábil deste mês está fechado. Reabra o período contábil antes de reabrir o extrato.',
    RECON_V2_STATEMENT_ALREADY_CLOSED: 'O extrato deste mês já está fechado.',
    RECON_V2_STATEMENT_CLOSED: 'O extrato deste mês já está fechado. Reabra para alterar.',
    RECON_V2_STATEMENT_NOT_CLOSABLE:
      'Este mês ainda não pode ser fechado: a prova de saldo aponta pendências.',
    RECON_V2_STATEMENT_NOT_CLOSED: 'O período não está fechado.',
    RECON_V2_STATEMENT_NOT_FOUND: 'Extrato não encontrado para este período.',
    RECON_V2_TRANSACTION_NOT_FOUND: 'Movimento não encontrado.',
    RECON_V2_TRANSACTION_NOT_IGNORED: 'Este movimento não está ignorado.',
    RECON_V2_TRANSACTION_NOT_IN_QUEUE: 'Este movimento já saiu da fila.',
    RECON_V2_UNBALANCED: 'Contas e ajustes não fecham com o valor do movimento.',
    RECON_V2_ADJUSTMENT_UNSUPPORTED_COMBINATION:
      'Este tipo de ajuste ainda não é suportado nesse lado da conciliação.',
    RECON_V2_ADJUSTMENT_SIGN_INVALID:
      'O valor do ajuste está com o sinal errado para esse tipo de ajuste.',
    RECON_V2_ADJUSTMENT_ROUNDING_TOLERANCE_EXCEEDED:
      'A diferença de arredondamento ultrapassa a tolerância permitida. Revise a alocação.',
    RECON_V2_ADJUSTMENT_JURISDICTION_MISMATCH:
      'Este tipo de ajuste existe apenas para entidades brasileiras.',
    RECON_V2_ADJUSTMENT_NOTE_REQUIRED:
      'Uma retenção não declarada no documento exige uma justificativa.',
    RECON_V2_ADJUSTMENT_CSRF_AMOUNT_INVALID:
      'Não foi possível desdobrar o total de CSRF em PIS, COFINS e CSLL.',
    RECON_V2_ADJUSTMENT_SLOT_NOT_RESOLVED:
      'Configure a conta contábil deste ajuste nas configurações contábeis.',
    RECON_V2_ADJUSTMENT_AGGREGATE_SPLIT_REQUIRED:
      'Informe o valor de cada tributo separadamente; um total agregado não pode ser lançado.',
    RECON_V2_ADJUSTMENT_ATTRIBUTION_REQUIRED:
      'Informe a qual título este ajuste pertence: o movimento baixa mais de um documento.',
    RECON_V2_ADJUSTMENT_CASH_MISMATCH:
      'A soma das baixas e dos ajustes não fecha com o valor da movimentação.',
    RECON_V2_ADJUSTMENT_SLOT_MAPPING_CONFLICT:
      'Esta conta de ajuste já está mapeada para outra conta contábil. Revise o mapeamento nas configurações contábeis.',
    RECON_V2_ADJUSTMENT_AMOUNT_UNREADABLE:
      'Não foi possível ler o valor gravado em um dos ajustes. Corrija o valor e refaça a conciliação.',
    // ALC-4243 (S7) write refusals. Copy by code; the 409 retry codes say "tente de novo".
    RECON_V2_ADJUSTMENT_AP_FIX_ON_BILL:
      'Este ajuste pertence à conta a pagar: corrija a retenção ou a tarifa na própria conta, não na conciliação.',
    RECON_V2_IDEMPOTENCY_KEY_REQUIRED:
      'A ação foi enviada sem identificador de repetição. Atualize a página e tente de novo.',
    RECON_V2_IDEMPOTENCY_KEY_REUSED:
      'Esta ação já foi enviada com outros dados. Atualize a página e refaça a decisão.',
    RECON_V2_IDEMPOTENCY_IN_PROGRESS:
      'Esta ação ainda está sendo processada. Aguarde alguns segundos e tente de novo.',
    RECON_V2_BATCH_TOO_LARGE: 'Confirme no máximo 100 movimentos por vez.',
    RECON_V2_BATCH_ITEM_NOT_CONFIRMABLE:
      'Este movimento não está no grupo Confirmar. Abra-o para decidir.',
    RECON_V2_TRANSACTION_ALREADY_RECONCILED:
      'Este movimento já foi conciliado. A fila foi atualizada.',
    RECON_V2_TREATMENT_UNSUPPORTED: 'Este tratamento ainda não é suportado.',
    RECON_V2_CREATE_KIND_UNSUPPORTED: 'Este tipo de documento ainda não pode ser criado por aqui.',
    RECON_V2_RECONCILE_TARGETS_REQUIRED: 'Selecione ao menos um documento ou classifique o valor.',
    RECON_V2_BATCH_ITEM_FAILED:
      'Não foi possível confirmar este movimento. Abra-o para ver o motivo.',
    RECON_V2_TRANSACTION_ALREADY_IGNORED:
      'Este movimento já está ignorado. Restaure-o antes de decidir.',
    RECON_V2_TARGET_TYPE_UNSUPPORTED: 'Este tipo de documento não pode ser conciliado por aqui.',
    RECON_V2_CREATE_INPUT_INVALID:
      'Faltam dados para criar o documento. Revise o fornecedor ou a conta de contrapartida.',
    RECON_V2_ADJUSTMENT_TYPE_UNSUPPORTED:
      'Este tipo de ajuste não existe nesse lado da conciliação.',
    RECON_V2_ADJUSTMENT_ATTRIBUTION_UNKNOWN:
      'O ajuste aponta para um documento que esta conciliação não baixa.',
    RECON_V2_ADJUSTMENT_WITHOUT_TARGET:
      'Selecione o documento ao qual os ajustes pertencem antes de conciliar.',
    // ALC-4369: a retention needs a document that received cash.
    RECON_V2_ADJUSTMENT_TARGET_NO_CASH:
      'Este ajuste está em um documento que recebeu 0,00 desta movimentação. Mova o ajuste para um documento que recebeu valor ou remova-o.',
    RECON_V2_ADJUSTMENT_EXCEEDS_ALLOCATION:
      'A retenção ou o desconto é igual ou maior que o valor baixado neste documento, e não sobraria valor recebido. Atribua o ajuste a um documento que recebeu valor suficiente desta movimentação.',
    RECON_V2_GL_POSTING_FAILED:
      'O lançamento contábil foi recusado e a conciliação foi desfeita. Revise a configuração contábil e concilie de novo.',
    RECON_V2_GL_POSTING_FAILED_DIRTY:
      'O lançamento contábil foi recusado e a conciliação não pôde ser desfeita. Não repita a ação: peça a um responsável para revisar este movimento.',
    RECON_V2_STATUS_CHANGED:
      'Este movimento mudou enquanto você decidia. A fila foi atualizada: tente de novo.',
    RECON_V2_BATCH_ITEM_AMBIGUOUS_ALLOCATION:
      'A sugestão não fecha com o valor do movimento. Abra-o para decidir.',
    RECON_V2_TARGET_NOT_FOUND: 'Documento não encontrado. Atualize a lista e selecione de novo.',
    RECON_V2_AR_SCHEDULE_ALLOCATION_INVALID:
      'As parcelas sugeridas desta fatura não estão mais em aberto nesses valores. Abra o movimento para decidir.',
    RECON_V2_AP_ADJUSTMENT_UNASSIGNED:
      'Informe a qual pagamento este ajuste pertence antes de conciliar.',
    RECON_V2_AP_SETTLEMENT_REJECTED:
      'A baixa da conta a pagar recusou esta conciliação. Revise a conta e o valor.',
    RECON_V2_AMOUNT_EXACT_INVALID:
      'Um dos valores informados é inválido. Revise os valores e concilie de novo.',
    RECON_V2_CREATE_AND_RECONCILE_REFUSED:
      'Não foi possível criar e conciliar o documento; nada foi gravado. Revise os dados e tente outra opção.',
    RECON_V2_AP_SETTLEMENT_RETRY:
      'A conta a pagar estava sendo atualizada por outra operação. Tente de novo.',
    // ALC-4325 (S1): internal-operation pick pre-flight (design §13). Nothing is saved on these.
    RECON_V2_INTERNAL_OP_BOOK_NOT_SUPPORTED:
      'Operação interna ainda não disponível para livros fora do Brasil.',
    RECON_V2_INTERNAL_OP_DIRECTION_MISMATCH:
      'Aplicação precisa de uma saída; resgate precisa de uma entrada.',
    // ALC-4358 (OQ7 A): "Imposto retido definitivo" needs the tax account "IRRF Definitivo – Outras
    // Operações" mapped. Nothing is saved. `getReconV2ErrorPresentation` adds the link to the Fiscal tab.
    RECON_V2_INTERNAL_OP_IRRF_FINAL_SLOT_NOT_MAPPED:
      'Falta mapear a conta contábil “IRRF Definitivo – Outras Operações” para esta empresa. Mapeie em Configurações → Contabilidade e Fiscal → Mapeamento de contas → Fiscal e tente de novo. Nada foi salvo.',
    RECON_V2_INTERNAL_OP_INVESTMENT_ACCOUNT_REQUIRED:
      'Escolha a conta de investimento de destino ou de origem.',
    RECON_V2_INTERNAL_OP_COUNTERPART_INVALID:
      'A conta escolhida não é uma conta de investimento ativa desta empresa.',
    RECON_V2_INTERNAL_OP_INVESTMENT_ACCOUNT_GL_NOT_CONFIGURED:
      'A conta de investimento não tem conta contábil configurada. Configure em Contas bancárias → a conta → Conta contábil e tente de novo.',
    RECON_V2_INTERNAL_OP_CURRENCY_MISMATCH: 'As duas contas precisam ter a mesma moeda confirmada.',
    RECON_V2_INTERNAL_OP_SWEEP_ACCOUNT_NEEDS_PAIR:
      'O saldo desta conta inclui a aplicação automática. Escolha o movimento par ou uma conta de investimento.',
    /** GL_NOT_CONFIGURED when the refusal names the account (`details.bankAccountName`). */
    internalOpGlNotConfiguredNamed:
      'A conta de investimento “{{bankAccountName}}” não tem conta contábil configurada. Configure em Contas bancárias → {{bankAccountName}} → Conta contábil e tente de novo.',
    // ALC-4323: the server message carries the date; this screen copy stays date-free.
    RECON_V2_HISTORICAL_LEDGER_PERIOD_DOCUMENT_BLOCKED:
      'Este movimento é anterior ao fim do histórico importado. Use “Já contabilizado no histórico importado”.',
    RECON_V2_HISTORICAL_LEDGER_OUTSIDE_PERIOD:
      'Este movimento é posterior ao fim do histórico importado. Concilie normalmente.',
    RECON_V2_HISTORICAL_LEDGER_END_DATE_NOT_SET:
      'A entidade ainda não tem data de fim do histórico importado. Defina em Configurações → Contábil & Fiscal → Entidades → editar a entidade → “Fim do histórico importado”.',
    RECON_V2_HISTORICAL_LEDGER_ACCOUNT_NOT_TIED_OUT:
      'Esta conta bancária não foi conferida quando o fim do histórico importado foi salvo. Salve a data de novo em Configurações → Contábil & Fiscal → Entidades → editar a entidade → “Fim do histórico importado”, com esta conta conferida.',
    // ALC-4326 (S2): route (b), the created counterpart (design §13). Nothing is saved on these.
    RECON_V2_INTERNAL_OP_USE_V2: 'Use a conciliação nova para aplicação e resgate.',
    RECON_V2_TRANSFER_CURRENCY_MISMATCH: 'As duas contas precisam ter a mesma moeda confirmada.',
    RECON_V2_INTERNAL_OP_INVESTMENT_GL_BALANCE_NOT_POSITIVE:
      'O saldo contábil desta conta de investimento é zero ou negativo na data do resgate. Lance o saldo de abertura da conta antes de conciliar resgates. Nada foi salvo.',
    RECON_V2_INTERNAL_OP_PICK_REQUIRED:
      'Escolha o tipo da operação interna (Aplicação, Resgate, Rendimento, Imposto retido ou Imposto retido definitivo). Nada foi salvo.',
    // ALC-4328 (S2b): the Resgate split. Nothing is saved on these.
    RECON_V2_INTERNAL_OP_RESGATE_OUT_OF_ORDER:
      'Já existe um resgate posterior contabilizado nesta aplicação. Desfaça-o antes de conciliar este resgate. Nada foi salvo.',
    RECON_V2_INTERNAL_OP_RESGATE_IRRF_BLANK:
      'Parte deste resgate é receita financeira e nenhum IRRF retido foi informado. Informe o IRRF que o banco reteve ou confirme que não houve retenção. Nada foi salvo.',
    RECON_V2_INTERNAL_OP_APLICACAO_BEFORE_POSTED_RESGATE:
      'Um resgate já contabilizado com receita financeira nesta aplicação usou o saldo desta data ou de uma data posterior. Desfaça primeiro esse resgate, concilie esta aplicação e depois o resgate de novo. Nada foi salvo.',
    // ALC-4329 (S3-AC3). Nothing is saved on these.
    RECON_V2_INTERNAL_OP_PAIR_AVAILABLE:
      'Já existe o movimento correspondente na conta de investimento. Pareie com ele. Nada foi salvo.',
    RECON_V2_INTERNAL_OP_ALREADY_RECLASSIFIED:
      'Este movimento já foi contabilizado pela conta corrente. Nada foi salvo.',
    // ALC-4329 round 1 (P3). Nothing is saved.
    RECON_V2_INTERNAL_OP_ROUTE_A_MIRROR_MISSING:
      'Não encontramos o resgate no extrato da aplicação. Importe o extrato da aplicação ou lance pela contabilidade.',
    RECON_V2_INTERNAL_OP_SWEEP_CROSSES_MONTH:
      'O par de aplicação automática cruza o fim do mês. Lance como Aplicação e Resgate na conta de investimento da aplicação automática.',
    // ALC-4359 round 1: the account was deleted while this write waited. Nothing is saved.
    RECON_V2_BANK_ACCOUNT_DELETED:
      'Esta conta bancária foi excluída antes de a conciliação rodar. Nada foi salvo. Atualize a tela.',
    // ALC-4235: the suggestions list is read in pages; both refusals are about the read only.
    RECON_V2_CANDIDATE_CURSOR_INVALID:
      'A lista de sugestões ficou desatualizada. Volte ao início da lista e tente de novo.',
    RECON_V2_CANDIDATE_AMOUNT_RANGE_INVALID:
      'O filtro de valor é inválido: informe valores a partir de zero e o mínimo não pode passar do máximo.',
    /** RECON_V2_CREATE_INPUT_INVALID of an internal operation, by the field the API names. */
    createInputInvalidField: {
      operationType:
        'Escolha o tipo da operação interna (Aplicação, Resgate, Rendimento, Imposto retido ou Imposto retido definitivo). Nada foi salvo.',
      withheldTaxAmount:
        'O IRRF retido só vale para resgate e precisa ser um valor igual ou maior que zero. Nada foi salvo.',
      counterpartAccountId:
        'Escolha o outro movimento do extrato ou uma conta de investimento, não os dois. Nada foi salvo.',
      sweepPartnerTransactionId:
        'O movimento escolhido não pode ser o outro lado: precisa ser da mesma conta, de sinal oposto, com o mesmo valor, pendente e a no máximo um dia útil. Só vale em conta cujo saldo do extrato inclui a aplicação automática. Nada foi salvo.',
    },
    /** ALC-4327 (OQ9, option A): DIRECTION_MISMATCH by pick and account kind. */
    directionMismatch: {
      withholding: 'Imposto retido é sempre uma saída da conta. Este movimento é uma entrada.',
      withholdingFinal:
        'Imposto retido definitivo é sempre uma saída da conta. Este movimento é uma entrada.',
      applicationChecking:
        'Aplicação é sempre uma saída da conta. Este movimento é uma entrada: se for um resgate, escolha Resgate.',
      rescueChecking:
        'Resgate é sempre uma entrada na conta. Este movimento é uma saída: se for uma aplicação, escolha Aplicação.',
      applicationInvestment:
        'Na conta de investimento, a aplicação é uma entrada. Este movimento é uma saída: se for um resgate, escolha Resgate.',
      rescueInvestment:
        'Na conta de investimento, o resgate é uma saída. Este movimento é uma entrada: se for uma aplicação, escolha Aplicação.',
      investmentPickNotValid:
        'Numa conta de investimento só valem Aplicação (entrada) e Resgate (saída). Rendimento, Imposto retido e Imposto retido definitivo não se lançam por aqui.',
    },
    /** ALC-4327 (N2): INVESTMENT_GL_BALANCE_NOT_POSITIVE with the balance and the day it was read. */
    balanceNotPositive:
      'O saldo contábil desta conta de investimento é {{balance}} em {{date}}. Lance o saldo de abertura antes de conciliar resgates. Nada foi salvo.',
    balanceNotPositiveNamed:
      'O saldo contábil da {{account}} é {{balance}} em {{date}}. Lance o saldo de abertura antes de conciliar resgates. Nada foi salvo.',
    // A day AFTER the Resgate is below 0,00: the cause is a broken later ledger, not a missing
    // opening balance (internal-operation-resgate-split.ts reports that day as `balanceDate`).
    balanceNotPositiveLater:
      'O saldo contábil desta conta de investimento fica {{balance}} em {{date}}, depois da data deste resgate. O razão da conta está negativo nesse dia: revise os lançamentos da conta até essa data antes de conciliar. Nada foi salvo.',
    balanceNotPositiveLaterNamed:
      'O saldo contábil da {{account}} fica {{balance}} em {{date}}, depois da data deste resgate. O razão da conta está negativo nesse dia: revise os lançamentos da conta até essa data antes de conciliar. Nada foi salvo.',
    /** Refusals that name the day that decides them (ALC-4328 P6 / O7, ALC-4329 P5). */
    dated: {
      RECON_V2_INTERNAL_OP_RESGATE_OUT_OF_ORDER:
        'Já existe um resgate de {{date}} contabilizado nesta aplicação. Desfaça-o antes de conciliar este resgate. Nada foi salvo.',
      RECON_V2_INTERNAL_OP_APLICACAO_BEFORE_POSTED_RESGATE:
        'O resgate de {{date}}, já contabilizado com receita financeira nesta aplicação, usou o saldo desta data ou de uma data posterior. Desfaça primeiro esse resgate, concilie esta aplicação e depois o resgate de novo. Nada foi salvo.',
      RECON_V2_INTERNAL_OP_SWEEP_CROSSES_MONTH:
        'O par de aplicação automática cruza o fim do mês ({{lineDate}} e {{partnerDate}}). Lance como Aplicação e Resgate na conta de investimento da aplicação automática.',
    },
    /** ALC-4327 (BT-4): the system-account slots an internal operation can miss, in the user's words. */
    slotNames: {
      OP_INVESTMENT_YIELD_REVENUE: 'Rendimento de Aplicações Financeiras',
      OP_INVESTMENT_IRRF_DEFINITIVE: 'IRRF Definitivo sobre Aplicações Financeiras',
      IRRF_DEFINITIVO: 'IRRF Definitivo – Outras Operações',
      IRRF_COMPENSAR: 'IRRF a Compensar',
      OP_IN_TRANSIT: 'Numerários em Trânsito',
      OP_INVESTMENT_SHORT_TERM: 'Investimentos de Curto Prazo',
      OP_INVESTMENT_LONG_TERM: 'Investimentos de Longo Prazo',
    },
    slotNotMapped:
      'Falta mapear a conta contábil “{{slots}}” para esta empresa. Mapeie em Configurações → Mapeamento de contas → Tesouraria e tente de novo. Nada foi salvo.',
    slotNotMappedUnnamed:
      'Falta mapear uma conta contábil do sistema para esta empresa. Mapeie em Configurações → Mapeamento de contas → Tesouraria e tente de novo. Nada foi salvo.',
    // A tax slot (IRRF a Compensar) lives on the Fiscal tab, not on Tesouraria.
    slotNotMappedTax:
      'Falta mapear a conta contábil “{{slots}}” para esta empresa. Mapeie em Configurações → Mapeamento de contas → Fiscal e tente de novo. Nada foi salvo.',
    slotNotMappedMixed:
      'Faltam mapear as contas contábeis “{{slots}}” para esta empresa. Mapeie em Configurações → Mapeamento de contas → Tesouraria e Fiscal e tente de novo. Nada foi salvo.',
    openAccountMapping: 'Abrir mapeamento de contas',
    openTaxAccountMapping: 'Abrir mapeamento fiscal',
    glReasons: {
      TAX_REGIME_NOT_CONFIGURED:
        'Configure o regime tributário da entidade para lançar o IR do come-cotas. Nada foi gravado.',
      CASH_MOVEMENT_CURRENCY_UNTRUSTED:
        'Moeda da conta não confirmada. Confirme a moeda em Configurações → Contas e concilie novamente. Nada foi contabilizado.',
      ZERO_OR_NEGATIVE_AMOUNT:
        'O valor do movimento é zero, então não há o que contabilizar. Revise a linha do extrato.',
      // ALC-4327 (Web-AC9): the other FAILED S0 / S0c reasons. Nothing was saved on any of them.
      CASH_MOVEMENT_REQUIRES_CONVERSION:
        'A moeda da conta bancária é diferente da moeda funcional do livro. Nada foi contabilizado.',
      WITHHOLDING_JURISDICTION_UNSUPPORTED:
        'Imposto retido na fonte só é contabilizado para empresas no Brasil nesta versão.',
      INVESTMENT_YIELD_DEBIT_WITHHOLDING_UNSUPPORTED:
        'Estorno de rendimento com imposto retido não é suportado. Concilie o imposto separadamente.',
      HISTORICAL_LEDGER_SPLIT_TRANSFER_REQUIRES_ACCOUNTING:
        'Uma perna desta transferência está no histórico importado e a outra tem tarifa ou retenção. Contabilize a outra perna manualmente.',
      HISTORICAL_LEDGER_SPLIT_TRANSFER_TRANSIT_NOT_IN_RAZAO:
        'O histórico importado não mostra como esta transferência foi contabilizada. Verifique o histórico importado antes de lançar.',
      // ALC-4325 K2b: an Aplicação / Resgate pick inside the imported history that could not be
      // settled. Nothing was saved (the group is compensated).
      INTERNAL_OP_HISTORY_SKIP_OUTSIDE:
        'O movimento não está mais dentro do histórico importado, então a Aplicação/Resgate não pode ser conciliada agora. Nada foi gravado. Atualize a fila e confira a data de fim do histórico importado.',
      INTERNAL_OP_HISTORY_SKIP_NO_SELF_LINK:
        'A conciliação da operação interna não foi criada, então nada foi gravado. Concilie de novo.',
      INTERNAL_OP_HISTORY_SKIP_KEPT_POSTED:
        'Este movimento já tem um lançamento contábil registrado, então não foi tratado como parte do histórico importado. Nada foi gravado. Revise a conciliação existente.',
      // ADR 0031: this screen cannot ask what to do with the bill's payments, so it says how to recover.
      RECONCILIATION_PAYMENT_MISMATCH:
        'O valor do banco é menor que os pagamentos da conta. Edite os pagamentos da conta para bater com o valor do banco e concilie de novo. Nada foi gravado.',
      RECONCILIATION_PAYMENT_MISMATCH_STALE:
        'A conta mudou desde que você abriu esta tela. Feche e abra a conciliação de novo.',
      RECONCILIATION_PAYMENT_MISMATCH_JUSTIFICATION_REQUIRED:
        'Essa conta tem um pagamento confirmado pelo banco com valor diferente. Edite os pagamentos da conta para bater com o valor do banco e concilie de novo. Nada foi gravado.',
      RECONCILIATION_PAYMENT_MISMATCH_BANK_CANCELLATION_REQUIRED:
        'Essa conta tem um pagamento enviado ao banco com valor diferente. Cancele no banco. Depois, edite os pagamentos da conta para bater com o valor do banco e concilie de novo. Nada foi gravado.',
      RECONCILIATION_PAYMENT_MISMATCH_REMAINDER_INVALID:
        'Uma conta aprovada precisa agendar o restante como um novo pagamento. Edite os pagamentos da conta para bater com o valor do banco e concilie de novo. Nada foi gravado.',
      RECONCILIATION_PAYMENT_MISMATCH_PAYMENT_POSTED:
        'Esse pagamento já tem lançamentos contábeis e não pode ser substituído aqui. Edite os pagamentos da conta para bater com o valor do banco e concilie de novo. Nada foi gravado.',
      RECONCILIATION_PAYMENT_MISMATCH_SEND_IN_FLIGHT:
        'Esse pagamento está sendo enviado ao banco agora. Espere o envio terminar e concilie de novo. Nada foi gravado.',
      RECONCILIATION_PAYMENT_MISMATCH_PERMISSION_REQUIRED:
        'Para substituir um pagamento confirmado ou que está no banco, você precisa da permissão de cancelar pagamentos do Contas a Pagar. Peça a quem tem essa permissão para concluir esta conciliação. Nada foi gravado.',
      // ALC-4326 carry 3: the Razão booked the pair directly and the other leg already posted transit.
      HISTORICAL_LEDGER_SPLIT_TRANSFER_TRANSIT_ALREADY_POSTED:
        'O histórico importado já contabilizou esta transferência direto entre os bancos, e a outra perna já foi lançada pela conta de trânsito. Desfaça esta conciliação de transferência, o que estorna o lançamento em trânsito, e pareie os dois movimentos de novo.',
      INVESTMENT_RESGATE_RETRY_REFUSED:
        'Este movimento é um resgate de aplicação e não pode ser relançado por nova tentativa. Desfaça a conciliação e escolha Resgate de novo. Nada foi contabilizado.',
      // ALC-4329 (S3-AC2, X1). Nothing was saved on either.
      CROSS_DAY_INVESTMENT_REDEMPTION_REQUIRES_ACCOUNTING:
        'Um resgate só é conciliado com o movimento da aplicação do mesmo dia. Aguarde esse movimento chegar. Nada foi gravado.',
      OP_IN_TRANSIT_MISSING:
        'O movimento da aplicação ainda não chegou, e a conta de numerários em trânsito não está configurada. Configure-a no plano de contas ou aguarde o movimento do mesmo dia. Nada foi gravado.',
      TRANSFER_SAME_ACCOUNT:
        'Os dois movimentos estão na mesma conta. Uma transferência precisa de duas contas; para uma aplicação automática na mesma conta, escolha a aplicação ou o resgate e indique o outro movimento. Nada foi gravado.',
      // ALC-4359 round 1: the post-commit posting found the account deleted. Nothing was saved.
      RECON_V2_BANK_ACCOUNT_DELETED:
        'Esta conta bancária foi excluída antes de o lançamento ser feito. Nada foi gravado. Atualize a tela.',
      // ALC-4235 (Charlie ACC-09): undone before the journal was recorded; the journal rolled back.
      MOVEMENT_POSTING_ROW_NO_SELF_LINK:
        'A conciliação desta operação interna não existe mais, então o lançamento contábil não foi gravado. Atualize a fila e concilie de novo.',
      // ALC-4235 (ccy-s3-ar): every AR receipt refusal, from the one registry in @alinea/types.
      ...AR_RECEIPT_REFUSAL_GL_REASONS_PT_BR,
    },
    openTaxRegimeSettings: 'Configurar regime tributário',
    // ALC-4327: the GL-binding refusal links to the investment account's GL field.
    openInvestmentAccountGlSettings: 'Configurar conta contábil',
    /** No coded answer: the connection dropped or a gateway answered 502/504. */
    unreachable:
      'Não foi possível confirmar a resposta do servidor. Tente de novo: a mesma ação é reenviada e não duplica o lançamento.',
  },
  proof: {
    noStatement: 'Não há extrato para este mês. Importe um extrato antes de fechar o período.',
    statement: 'Extrato em {{date}}',
    statementStale: 'Extrato',
    ledger: 'Razão',
    /** The ledger cell names the GL account by code when the proof carries one. */
    ledgerAccount: 'Razão {{code}}',
    difference: 'Diferença',
    /**
     * The one banner for a missing month-end statement (owner declutter, ORCH Q18): it replaces
     * the former "Último saldo conhecido" paragraph, the red warning line and the amber tab notes.
     */
    staleBanner: 'Extrato parado em {{date}}. Atualize para fechar {{month}}.',
    /** Third banner action: the month-end balance typed on the entity screen (ALC-4360). */
    typeBalance: 'Digitar saldo',
    /** Keyed by `ReconV2BalanceProof.unavailableReason` (ALC-4237). */
    unavailableReason: {
      CARD_NOT_SUPPORTED: 'A prova de saldo para cartões ainda não está disponível.',
      LEDGER_ACCOUNT_RESTRICTED:
        'A conta contábil desta conta bancária é restrita e você não tem acesso a ela.',
      LEDGER_SHARED_GL_ACCOUNT:
        'Mais de uma conta bancária usa a mesma conta contábil, então não há saldo do razão por conta.',
      LEDGER_PERIOD_NOT_FOUND:
        'Não há período contábil que contenha o fim deste mês, então não há saldo do razão para comparar.',
      LEDGER_PERIOD_NOT_MONTHLY:
        'O calendário contábil desta entidade não é mensal, então o saldo do razão não corresponde a um mês isolado.',
    },
    /** Which source stated the statement balance (`statement.source`). */
    statementSource: {
      USER_DECLARED: 'Saldo digitado do extrato',
      STATEMENT_CLOSING: 'Saldo de fechamento do extrato',
      DAILY_BALANCE: 'Saldo diário da conta',
      LAST_TRANSACTION_BALANCE: 'Saldo após o último movimento',
    },
    /** A statement balance a person typed (`statement.source = USER_DECLARED`, ALC-4360). */
    typed: {
      label: 'Saldo digitado em {{date}}',
      closeDescription:
        'O saldo digitado e o razão batem em {{date}}. Fechar trava o período nesta conta: novos movimentos com data de {{month}} vão exigir reabertura.',
      closeNote: 'Este saldo foi digitado por {{user}} em {{date}}; não veio de um extrato.',
      closeNoteNoUser: 'Este saldo foi digitado em {{date}}; não veio de um extrato.',
      closeNoteBare: 'Este saldo foi digitado; não veio de um extrato.',
    },
    /**
     * The newest known balance of an account whose month-end statement is stopped or missing
     * (`lastAvailable`, ALC-4370). Every figure carries its own date; none is the month-end proof.
     */
    lastAvailable: {
      // "Banco", not "Extrato": the newest balance is often an Open Finance day or a last
      // transaction balance, and the banner under the figures says the statement is stopped.
      bank: 'Banco em {{date}}',
      /** Tooltips of the ledger and difference cells: the figures are the ones of that date. */
      ledger: 'Razão em {{date}}',
      difference: 'Diferença em {{date}}',
    },
    /** `pendingBreakdown`: a fila alcança meses anteriores (decisão D-S2b). */
    pending: {
      thisPeriod: 'Na fila deste período',
      earlierPeriods: 'Na fila de meses anteriores',
    },
    unavailableTitle: 'Prova de saldo indisponível para cartões',
    unavailableBody:
      'A prova por fatura chega em uma próxima entrega. A fila de decisões do cartão funciona normalmente.',
    /**
     * Progress by COUNT (ORCH Q18): reconciled movements of the month over reconciled + still to
     * explain (removed-by-bank lines count as to explain; ignored ones count in neither). It
     * never reads "N de N" while a row is open, by construction.
     */
    progress: '{{reconciled}} de {{total}} conciliadas, {{auto}} pelo Alchemy',
    balanced: 'Extrato e razão batem',
    viewLog: 'Ver registro',
    fxLine: '{{amount}} de variação cambial não ajustada',
    fxHint: 'Bloqueia o fechamento até a remensuração rodar.',
    residualLine: '{{amount}} no razão sem movimento no extrato',
    residualHint: 'Bloqueia o fechamento até ser explicado.',
    conversion: '{{from}} → {{to}} a {{rate}} · {{source}} de {{date}}',
    reconnect: 'Reconectar',
    importOfx: 'Importar OFX',
    close: 'Fechar {{month}}',
    closedPill: '{{month}} fechado',
    closedAt: 'Fechado em {{date}}',
    reopen: 'Reabrir',
    blockersTitle: 'O que falta para fechar',
    blockers: {
      QUEUE_NOT_EMPTY: 'Ainda há movimentos a explicar',
      DIFFERENCE_NOT_ZERO: 'A diferença ainda não é zero',
      UNEXPLAINED_RESIDUAL: 'Há valor no razão sem movimento no extrato',
      UNREMEASURED_FX: 'Variação cambial não ajustada',
      STATEMENT_BALANCE_MISSING: 'Sem saldo de extrato no fim do mês',
      BANK_GL_ACCOUNT_UNMAPPED: 'Conta contábil do banco não mapeada',
      FX_RATE_MISSING: 'Sem taxa de câmbio para o fim do mês',
      ALREADY_CLOSED: 'Período já fechado',
      PERIOD_IN_PROGRESS: 'O mês ainda não terminou',
      STATEMENT_STALE:
        'O extrato está desatualizado. Atualize a conexão ou importe o extrato antes de fechar.',
      PROVIDER_REMOVED_ACTIVE_GL_one:
        'O banco removeu {{count}} linha que ainda tem lançamento contábil ativo. Resolva-a antes de fechar.',
      PROVIDER_REMOVED_ACTIVE_GL_other:
        'O banco removeu {{count}} linhas que ainda têm lançamento contábil ativo. Resolva-as antes de fechar.',
    },
    closeDialog: {
      title: 'Fechar {{month}} para {{account}}?',
      description:
        'Extrato e razão batem em {{date}}. Fechar trava o período nesta conta: novos movimentos com data de {{month}} vão exigir reabertura.',
      confirm: 'Fechar {{month}}',
    },
    reopenDialog: {
      title: 'Reabrir {{month}} para {{account}}?',
      description: 'A reabertura fica registrada na trilha de auditoria. Informe o motivo.',
      reasonLabel: 'Motivo da reabertura',
      reasonPlaceholder: 'Ex.: extrato complementar recebido em 03/09',
      confirm: 'Reabrir',
    },
    toasts: {
      closed: '{{month}} fechado para {{account}}',
      reopened: '{{month}} reaberto para {{account}}',
      closeFailed: 'Não foi possível fechar o período.',
      reopenFailed: 'Não foi possível reabrir o período.',
    },
  },
  /**
   * Bank lines the provider removed but the ERP kept because they carry active posted GL
   * (ALC-4363, decisão ORCH Q11 = A): reconciling items, outside the decision groups.
   */
  removed: {
    title: 'Removidas pelo banco',
    /** Right of the heading: the one phrase that says what "removed" means for the proof. */
    subtitle: 'Sem linha no extrato, ainda no razão',
    /** The row's single line (owner declutter): the heading already says why it is here. */
    removedAt: 'Removida em {{date}}',
    /** Tag on an item that belongs to another month than the one on screen (ORCH Q15). */
    fromMonth: 'Lançada em {{month}} de {{year}}',
    undo: 'Desfazer conciliação',
    /** Visible row text, no button: the active GL is a movement journal no reconciliation holds. */
    noGroup: 'Sem conciliação para desfazer. Estorne o lançamento em Contábil.',
    /** Visible row text, no button: the undo would be refused until the month is reopened. */
    undoBlocked: {
      STATEMENT_CLOSED: 'Reabra o extrato de {{month}} para desfazer.',
      ACCOUNTING_PERIOD_CLOSED: 'Reabra o período contábil de {{month}} para desfazer.',
      // The closed month is not the month on screen: it names its year too.
      STATEMENT_CLOSED_withYear: 'Reabra o extrato de {{month}} de {{year}} para desfazer.',
      ACCOUNTING_PERIOD_CLOSED_withYear:
        'Reabra o período contábil de {{month}} de {{year}} para desfazer.',
    },
  },
  tabs: {
    toExplain: 'A explicar',
    reconciled: 'Conciliados',
    ignored: 'Ignorados',
  },
  search: {
    placeholder: 'Nome, CNPJ ou valor',
    label: 'Buscar na fila',
    clear: 'Limpar busca',
    filters: 'Filtros',
    direction: 'Direção',
    both: 'Entradas e saídas',
    debitOnly: 'Só saídas',
    creditOnly: 'Só entradas',
  },
  /** Group names: the panel kicker and the section headings (the filter chips are gone). */
  groups: {
    CONFIRM: 'Confirmar',
    RESOLVE_DIFF: 'Resolver diferença',
    REVIEW: 'Revisar',
    IDENTIFY: 'Sem documento',
    sectionTitle: {
      CONFIRM: 'Confirmar',
      RESOLVE_DIFF: 'Resolver diferença',
      REVIEW: 'Revisar antes de conciliar',
      IDENTIFY: 'Sem documento',
    },
    sectionSubtitle: {
      CONFIRM: 'Contraparte, valor e data batem.',
      RESOLVE_DIFF: 'Documento certo, valor diferente. Diga o que é a diferença.',
      REVIEW: 'Bate, mas tem efeito contábil ou de controle.',
      IDENTIFY: 'Nenhum documento correspondente.',
    },
    batch: 'Conciliar {{count}}',
    /** Above RECON_V2_BATCH_CONFIRM_MAX: one batch sends the first {{count}} of {{total}}. */
    batchCapped: 'Conciliar {{count}} de {{total}}',
    batchDialog: {
      title: 'Conciliar {{count}} movimentos de uma vez?',
      titleCapped: 'Conciliar {{count}} de {{total}} movimentos agora?',
      rest_one: 'O movimento restante fica na fila para a próxima rodada.',
      rest_other: 'Os outros {{count}} movimentos ficam na fila para a próxima rodada.',
      description:
        'Cada movimento vira uma conciliação própria com o documento sugerido. Os lançamentos contábeis são feitos agora e podem ser desfeitos item a item no registro.',
      confirm: 'Conciliar {{count}}',
    },
    batchToast_one: '{{count}} movimento conciliado',
    batchToast_other: '{{count}} movimentos conciliados',
    batchPartial: '{{ok}} conciliados, {{failed}} com erro',
  },
  row: {
    delta: 'Δ {{amount}}',
    select: 'Selecionar {{name}}',
  },
  empty: {
    entities: 'Nenhuma entidade disponível para conciliação.',
    accounts: 'Nenhuma conta disponível para esta entidade e período.',
    groupTitle: 'Nada em {{group}}',
    groupBody: 'Ainda há itens em {{other}}.',
    goTo: 'Ir para {{other}}',
    seeAll: 'Ver todos',
    searchTitle: 'Nada encontrado para “{{q}}”',
    searchBody: 'Limpe a busca para ver a fila inteira.',
    staleTitle: 'Sem movimentos para explicar',
    staleBody:
      'O extrato do {{bank}} parou em {{date}}. Reconecte ou importe o OFX para continuar.',
    queueTitle: 'Fila vazia',
    queueBody: 'Todos os movimentos de {{month}} estão explicados.',
    /** The queue has no decision left, but removed items still block the close (ORCH Q15). */
    removedOnlyTitle: 'Nenhum movimento a decidir',
    removedOnlyBody:
      'Restam só as linhas removidas pelo banco, acima. Resolva cada uma para fechar.',
    closedTitle: '{{month}} fechado',
    closedBody: 'Novos movimentos com data de {{month}} vão exigir reabertura do período.',
    readyTitle: 'Nada para explicar',
    readyBody:
      'Extrato e razão batem em {{date}}. Feche {{month}} nesta conta para travar o período.',
    detailTitle: 'Nenhum item selecionado',
    detailBody: 'Escolha um status com itens na lista ao lado, ou use as teclas 1–5.',
  },
  decision: {
    raw: 'No extrato',
    why: 'Por que o Alchemy sugere isto',
    source: {
      RULES: 'Regra',
      HISTORICAL: 'Histórico',
      AI: 'IA',
      MANUAL: 'Manual',
    },
    criteriaTitle: '{{matched}} de {{total}} critérios batem',
    criteria: {
      COUNTERPARTY: 'Contraparte',
      AMOUNT: 'Valor',
      DATE: 'Data',
      REFERENCE: 'Referência',
      METHOD: 'Forma',
    },
    criteriaState: {
      ok: 'bate',
      delta: 'diferença',
      fail: 'não bate',
    },
    open: 'Abrir',
    document: 'Documento',
    suggested: 'Vínculo sugerido',
    columns: {
      bank: 'No extrato',
      doc: 'No Alchemy',
    },
    // ALC-4364: justificativa e documento de cada ajuste (mesma regra do pré-voo do servidor).
    adjustmentFields: {
      note: 'Justificativa',
      notePlaceholder: 'Ex.: cliente reteve o IRRF e vai emitir o informe',
      optional: 'opcional',
      required: 'obrigatória',
      requiredMasculine: 'obrigatório',
      noteRequiredHint:
        'Explique por que o cliente reteve: a retenção ainda não está declarada no documento.',
      document: 'Documento',
      documentPlaceholder: 'Escolha o documento',
      documentRequiredHint: 'Este ajuste vale para um documento só. Escolha qual.',
      blockedToast:
        'Falta a justificativa ou o documento de um ajuste. Preencha os campos destacados.',
    },
    // ALC-4364: quitar o documento inteiro quando a retenção o fecha sem o banco cobrir tudo.
    allocation: {
      settleFull: 'Quitar o documento inteiro',
      settleFullHint:
        'Quita o valor em aberto; a diferença para o banco vira ajuste (por exemplo, uma retenção).',
      // ALC-4369: true whether or not anything is ticked: 0,00 also happens when an earlier
      // document simply takes the whole movement (TED 1.000, NF A 1.000 selected first).
      noCash:
        "O movimento não tem valor sobrando para este documento: ele fica de fora desta conciliação. Desmarque-o ou, se outro documento estiver com 'Quitar o documento inteiro' marcado, desmarque essa opção para liberar valor.",
    },
    diff: {
      title: 'O que é a diferença',
      hint: 'Troque o tipo se estiver errado.',
      addRow: 'Adicionar ajuste',
      removeRow: 'Remover ajuste',
      type: 'Tipo do ajuste',
      amount: 'Valor',
      csrfShortcut: 'CSRF (PIS + COFINS + CSLL)',
      basis: 'Base',
      account: '{{side}} em {{code}} {{name}}',
      debit: 'Débito',
      credit: 'Crédito',
      explained: 'Diferença de {{amount}} totalmente explicada',
      notComparable: 'Sem diferença medida: a sugestão tem um lançamento que esta tela não mostra',
      unexplainedMore: 'Não fecha: {{amount}} a mais no banco com esses tipos',
      unexplainedLess: 'Não fecha: {{amount}} a menos no banco com esses tipos',
      adjustUntilZero: 'Ajuste os tipos até a diferença fechar.',
      discountKind: 'Tipo do desconto',
      discountConditional: 'Desconto condicional',
      discountUnconditional: 'Desconto incondicional (dedução de receita)',
    },
    review: {
      title: 'Sinal de controle',
      whatHappens: 'O que acontece ao conciliar',
      signals: {
        PAYMENT_BEFORE_INVOICE: 'Nota emitida depois do pagamento',
        PAYMENT_BEFORE_APPROVAL: 'Conta a pagar ainda em revisão',
        DIRECTION_MISMATCH: 'Sinal do movimento não bate com a descrição',
        POSSIBLE_DUPLICATE: 'Possível pagamento em duplicidade',
      },
      effects: {
        PAYMENT_BEFORE_INVOICE:
          'O valor fica em adiantamentos a fornecedores até a nota ser lançada. Quando a conta a pagar existir, o adiantamento é baixado contra ela.',
        PAYMENT_BEFORE_APPROVAL:
          'A conciliação fica marcada como exceção de controle e quem aprova contas a pagar recebe um aviso para concluir a revisão. O lançamento contábil só é feito depois da aprovação.',
        DIRECTION_MISMATCH:
          'Registrar como aplicação lança a saída contra a conta de investimento. Enviar para correção devolve o movimento para a importação sem lançamento.',
        POSSIBLE_DUPLICATE:
          'Conciliar mesmo assim registra a exceção na trilha de auditoria para revisão do financeiro.',
      },
      treatment: {
        SETTLEMENT: 'Quitação da conta',
        ADVANCE: 'Adiantamento a fornecedor',
        INTERNAL_APPLICATION: 'Aplicação financeira',
      },
      actions: {
        registerAdvance: 'Registrar adiantamento',
        settleAnyway: 'Conciliar como quitação mesmo assim',
        settleAndNotify: 'Conciliar e avisar aprovação',
        openBill: 'Abrir conta a pagar',
        registerApplication: 'Registrar aplicação',
        settleDuplicate: 'Conciliar mesmo assim',
      },
      /** Outside the demo: the effect of the only treatment the API books (S7: SETTLEMENT). */
      effectsLive: {
        PAYMENT_BEFORE_INVOICE:
          'Registrar como adiantamento chega em breve. Conciliar como quitação baixa a conta a pagar com este pagamento, mesmo com a nota emitida depois.',
        DIRECTION_MISMATCH:
          'Registrar como aplicação financeira chega em breve; nada é lançado a partir desta sugestão.',
      },
      noTreatment:
        'Para seguir agora, recuse a sugestão (R): o movimento vai para Sem documento, onde você escolhe outro documento ou cria um (Criar lançamento). Ou ignore o movimento (I).',
      noTreatmentNoCandidate:
        'Para seguir agora, ignore o movimento (I) ou aguarde uma nova sugestão.',
      anomaly: {
        question: 'O que aconteceu?',
        application: 'Foi uma aplicação',
        importError: 'Erro na importação do extrato',
      },
    },
    identify: {
      needsTarget: {
        title: 'Escolha um documento para conciliar com esses ajustes.',
        debit:
          'Uma classificação sozinha não concilia o movimento. Se for uma tarifa sem documento, crie a tarifa bancária: a conta é criada e conciliada de uma vez.',
        credit:
          'Uma classificação sozinha não concilia o movimento. Selecione o recebível ou crie o documento pelo menu Criar.',
      },
      title: 'Sugestões',
      /** Right column of a suggestion: movement − document, number only ("dif. 671,50"). */
      diff: 'dif. {{amount}}',
      openCount_one: '{{count}} em aberto',
      openCount_other: '{{count}} em aberto',
      // Second line of a suggestion: "A pagar 5580455005 · Comp. 09/2026 · Venc. 10/10/2026".
      // A part the document does not have is left out, never shown as a dash.
      competence: 'Comp. {{date}}',
      dueOn: 'Venc. {{date}}',
      loadingMore: 'Carregando mais…',
      loadMoreError: 'Não foi possível carregar mais sugestões.',
      loadMoreRetry: 'Tentar novamente',
      amountFilter: {
        label: 'Valor em aberto',
        min: 'Valor mínimo',
        max: 'Valor máximo',
        minHandle: 'Controle do valor mínimo',
        maxHandle: 'Controle do valor máximo',
        clear: 'Limpar',
        invalid: 'O valor mínimo não pode passar do máximo.',
        // The upper end of the slider: no ceiling.
        orMore: '{{amount}}+',
        // What a screen reader says for the maximum handle when there is no ceiling.
        valueOrMore: '{{amount}} ou mais',
        // What a screen reader says for the minimum handle when there is no floor.
        valueNoMinimum: 'sem limite',
      },
      searchPlaceholder: 'Fornecedor, nº do documento ou valor',
      loadingCandidates: 'Carregando documentos em aberto…',
      type: 'Tipo',
      typeAP: 'A pagar',
      typeAR: 'A receber',
      typeTransfer: 'Transferências',
      typeAll: 'Todos os tipos',
      emptyFilter: 'Nenhuma conta em aberto neste filtro. Use Criar para registrar este movimento.',
      emptySearch: 'Nenhuma conta em aberto para “{{q}}”. Use Criar para registrar este movimento.',
      hints: {
        TRANSFER_PAIR:
          'Movimento de mesmo valor e mesmo dia no {{account}}, na mesma empresa. Selecione para registrar os dois lados como transferência.',
        CNPJ_CLUSTER: '{{why}}',
        INSTALLMENT: '{{why}}',
        FEE_LIKE:
          'Parece encargo bancário. Registre como tarifa: a conta a pagar é criada e conciliada de uma vez.',
        weak: 'A sugestão anterior batia em {{matched}} de {{total}} critérios e foi descartada.',
        rejected: 'Você recusou a sugestão. Escolha outra conta ou crie uma nova.',
        nomatch: 'Nenhuma conta com esse valor e contraparte. Mostrando as mais próximas.',
      },
      hintActions: {
        TRANSFER_PAIR: 'Registrar transferência',
        CNPJ_CLUSTER: 'Criar cliente e regra',
        INSTALLMENT: 'Criar recorrência',
        FEE_LIKE: 'Registrar tarifa',
      },
      exact: 'Valor exato',
      discarded: 'Sugestão descartada',
      rejectedTag: 'Recusada por você',
      // Relative to the movement date: overdue ("Venceu há") is an action item and reads amber;
      // not yet due ("Vence em") is neutral.
      dueSameDay: 'Vence no mesmo dia',
      dueAfter_one: 'Vence em {{count}} dia',
      dueAfter_other: 'Vence em {{count}} dias',
      dueBefore_one: 'Venceu há {{count}} dia',
      dueBefore_other: 'Venceu há {{count}} dias',
      sameDay: 'Mesmo dia',
      partial: 'Pagamento parcial',
      partialApplies: 'aplica {{applied}}, saldo de {{remaining}} fica em aberto',
      partialFull: 'cobre o valor integral',
      exceeds: 'A transação excede as contas em {{amount}}. Como classificar?',
      accountsExceed: 'As contas excedem a transação em {{amount}}. Como classificar?',
      classified: 'Diferença de {{amount}} classificada',
      discardedTitle: 'Sugestões descartadas',
      discardedReason: {
        LOW_CRITERIA: 'Poucos critérios batem',
        USER_REJECTED: 'Recusada por você',
      },
      create: {
        comingSoonHint: 'Ação em breve: por enquanto, selecione documentos ou classifique o valor.',
        cluster: 'Cliente e regra para este CNPJ',
        installment: 'Recorrência (parcelas {{from}} a {{to}})',
        transfer: 'Transferência entre contas',
        paidBill: 'Conta a pagar já paga',
        fee: 'Tarifa, IOF ou encargo',
        receipt: 'Recebimento avulso',
        internalOp: 'Conciliar como operação interna',
        vendorAdvance: 'Adiantamento a fornecedor (sem conta)',
      },
      createDialog: {
        confirm: 'Criar e conciliar',
        cancel: 'Cancelar',
        vendor: 'Fornecedor',
        vendorOptional: 'Fornecedor (opcional)',
        vendorPlaceholder: 'Buscar fornecedor',
        vendorDefaultHint:
          'Sem fornecedor, a tarifa vai para o fornecedor padrão de tarifas bancárias.',
        counterpart: 'Conta de contrapartida',
        counterpartPlaceholder: 'Selecione a outra conta',
        noCounterpart:
          'Esta entidade não tem outra conta para receber o outro lado da transferência.',
        descriptionLabel: 'Descrição (opcional)',
        descriptionPlaceholder: 'Aparece no documento criado',
        FEE_BILL: {
          title: 'Criar tarifa e conciliar',
          description:
            'Cria uma conta a pagar de tarifa bancária de {{amount}} e concilia com “{{description}}”.',
        },
        VENDOR_ADVANCE_BILL: {
          title: 'Criar adiantamento e conciliar',
          description:
            'Cria um adiantamento a fornecedor de {{amount}}, sem conta de origem, e concilia com “{{description}}”.',
        },
        INTERNAL_TRANSFER: {
          title: 'Criar transferência e conciliar',
          description:
            'Cria o outro lado da transferência de {{amount}} na conta escolhida e concilia os dois lados.',
        },
        INTERNAL_OPERATION: {
          title: 'Conciliar como operação interna',
          description:
            'Registra “{{description}}” ({{amount}}) como operação interna. Escolha o tipo e, para aplicação ou resgate, a conta de investimento.',
        },
        // ALC-4327 (Web-AC1..AC6): the internal-operation picker.
        internalOp: {
          typeLabel: 'Tipo da operação',
          types: {
            YIELD: 'Rendimento',
            APPLICATION: 'Aplicação',
            RESCUE: 'Resgate',
            WITHHOLDING_TAX: 'Imposto retido na fonte',
            WITHHOLDING_TAX_FINAL: 'Imposto retido definitivo',
          },
          // ALC-4358 (OQ7 A, AC5): a suggestion only, never a pre-selection.
          finalWithholdingHint:
            'Este movimento parece imposto de renda que não é de aplicação financeira (por exemplo, sobre operação de câmbio). Se foi retido como tributação definitiva e não pode ser compensado, escolha “Imposto retido definitivo”.',
          bankClassification: 'Classificação do banco: {{type}}',
          investmentAccount: 'Conta de investimento',
          investmentAccountPlaceholder: 'Selecione a conta de investimento',
          noInvestmentAccount: 'Esta empresa ainda não tem conta de investimento ativa.',
          investmentCurrencyNotSupported:
            'Em {{currency}}: operação interna só com contas em BRL nesta versão.',
          createInvestmentAccount: 'Criar conta de investimento',
          createAccountFailed: 'Não foi possível criar a conta de investimento. Nada foi gravado.',
          createAccountErrors: {
            BANK_ACCOUNT_COA_ASSIGNMENT_REQUIRED:
              'A empresa ainda não tem um plano de contas ativo. Configure o plano de contas antes de criar a conta. Nada foi gravado.',
            BANK_ACCOUNT_GL_OFFSET_INVALID:
              'A conta contábil escolhida não serve para uma conta de investimento. Nada foi gravado.',
            INVALID_CURRENCY_CODE: 'A moeda escolhida não é válida. Nada foi gravado.',
            FUNCTIONAL_CURRENCY_MISMATCH:
              'A moeda da conta precisa ser a moeda funcional da empresa. Nada foi gravado.',
            FUNCTIONAL_CURRENCY_UNRESOLVED:
              'A empresa ainda não tem moeda funcional definida. Defina antes de criar a conta. Nada foi gravado.',
            FORBIDDEN: 'Você não tem permissão para criar contas bancárias. Nada foi gravado.',
          },
          createdAccountNotListed:
            'A conta foi criada, mas só contas de investimento ativas desta empresa, em BRL, podem ser escolhidas aqui.',
          openGlSettings: 'Configurar conta contábil',
          // BT-1: the route text follows the server's route read (route + pairing), never a guess.
          route: {
            checking: 'Conferindo o caminho da conciliação…',
            checkFailed: 'Não foi possível conferir o caminho desta conciliação com o servidor.',
            created: 'Será criado o movimento de {{amount}} na {{account}}',
            mirror:
              'Será pareado com o movimento correspondente em {{account}}. Nenhum movimento novo é criado.',
            mirrorSameDay:
              'Será pareado com o movimento de {{money}} já importado em {{account}}, do mesmo dia. Nenhum movimento novo é criado.',
            awaiting:
              'O movimento da aplicação ainda não chegou ao extrato de {{account}}. Os {{money}} saem da conta e ficam em numerários em trânsito até ele ser importado.',
            completesAwaiting:
              'Completa uma aplicação que aguardava o outro lado: os {{money}} saem de numerários em trânsito e entram em {{account}}.',
            // The same pairing seen from the other side of the pair: a line on the investment
            // account that opens the wait, and a line on the checking account that completes it.
            awaitingInvestmentSide:
              'O movimento da conta corrente ainda não chegou ao extrato. Os {{money}} entram em {{account}}; a contrapartida fica em numerários em trânsito até ele ser importado.',
            completesAwaitingCheckingSide:
              'Completa uma aplicação que aguardava o outro lado: os {{money}} saem da conta e dão baixa em numerários em trânsito.',
            sweep:
              'Pareado com o outro lado do mesmo extrato. Sem lançamento contábil: o saldo do extrato já soma os dois.',
          },
          // ALC-4327 (Web-AC6): the other half of a sweep line.
          sweep: {
            label: 'Outro lado da aplicação automática',
            hint: 'O saldo desta conta já soma a aplicação automática: escolha o outro movimento do mesmo extrato ou uma conta de investimento.',
            modePartner: 'Movimento par do extrato',
            modeInvestment: 'Conta de investimento',
            partnerLabel: 'Movimento par',
            partnerPlaceholder: 'Selecione o movimento par',
            partnerLoading: 'Buscando o movimento par…',
            partnerLoadFailed: 'Não foi possível buscar o movimento par.',
            partnerNone:
              'Nenhum movimento par encontrado: precisa ser da mesma conta, de sinal oposto, com o mesmo valor, pendente e a no máximo um dia útil.',
            partnerTruncated:
              'A lista de movimentos do mês está incompleta. Se o par não aparece, filtre a fila por valor e tente de novo.',
          },
          noJournal: 'Sem lançamento contábil',
          noJournalHint:
            'O movimento está dentro do histórico importado; o razão importado já o contabilizou.',
          loadingAccounts: 'Carregando contas…',
          accountsLoadFailed: 'Não foi possível carregar as contas bancárias desta empresa.',
          retry: 'Tentar de novo',
          // ALC-4327 (Web-AC5, BT-2): the Resgate split and the IRRF the bank withheld.
          irrf: {
            label: 'IRRF retido (informe do banco)',
            hint: 'O IRRF que o banco reteve neste resgate, como consta no informe de rendimentos. Em branco, o sistema avisa antes de lançar sem IRRF quando houver receita financeira.',
            warningTitle: 'Parte deste resgate é receita financeira',
            warningBody:
              'O resgate de {{amount}} passa do saldo contábil da aplicação: {{excess}} é receita financeira e nenhum IRRF retido foi informado. Informe o IRRF que o banco reteve ou confirme que não houve retenção. Nada foi salvo.',
            // Once an IRRF is typed the warning no longer says none was informed.
            warningBodyTyped:
              'O resgate de {{amount}} passa do saldo contábil da aplicação: {{excess}} é rendimento. O IRRF informado é {{tax}}. Nada foi salvo.',
            principal: 'Principal resgatado da aplicação',
            income: 'Receita financeira',
            tax: 'IRRF retido',
            incomeBreakdown: 'A receita soma o rendimento de {{excess}} e o IRRF de {{tax}}.',
            consent: 'Confirmo que o banco não reteve IRRF neste resgate (IRRF 0,00)',
          },
        },
      },
      backToSuggestions: '← Voltar para sugestões',
      // ALC-4327 (Web-AC7): a line dated on or before the entity's "Fim do histórico importado".
      historical: {
        action: 'Já contabilizado no histórico importado',
        hintNoDate:
          'Movimento de {{date}}, dentro do histórico importado. O razão importado já o contabilizou: concilie sem documento.',
        // P8: a line classified as Aplicação / Resgate is settled here without the investment
        // account's own movement; "Operação interna" records it (route (b), no journal, O1).
        internalOpHint:
          'Use “Operação interna” para também registrar o movimento na conta de investimento.',
        noJournal: 'Sem lançamento contábil',
        hiddenCreate:
          'Tarifa e adiantamento não são criados para movimentos dentro do histórico importado: o razão importado já os contabilizou.',
      },
    },
    footer: {
      transaction: 'Transação',
      accounts: 'Contas',
      adjustments: 'Ajustes',
      difference: 'Diferença',
      reconcile: 'Conciliar',
      reconcileWithAdjustments: 'Conciliar com ajustes',
      reconcileTransfer: 'Registrar transferência',
      reconcileBatchHint: 'Enter',
      reject: 'Não é este',
      rejectHint: 'R',
      ignore: 'Ignorar',
      undo: 'Desfazer',
      ignoreHint: 'I',
      create: 'Criar lançamento',
      /** Key bar under the list. */
      navigate: 'navegar',
      search: 'buscar',
      /** Collapsed shortcut sheet when the decision footer is under ~900px. */
      shortcuts: 'Atalhos',
      pairWith: 'Par: {{account}} · {{date}} · {{amount}}',
    },
    ignoreDialog: {
      title: 'Ignorar este movimento?',
      description:
        'Movimentos ignorados não entram na prova de saldo e ficam listados no fechamento do mês como exceção. O motivo é obrigatório.',
      reasonLabel: 'Motivo',
      reasonPlaceholder: 'Ex.: estorno duplicado pelo banco, já tratado em julho',
      confirm: 'Ignorar',
    },
    undoDialog: {
      nothing: 'Nada para desfazer nesta sessão.',
    },
    toasts: {
      reconciled: 'Conciliado com {{ref}}',
      reconciledWithAdjustments: 'Conciliado com {{ref}} e {{adjustments}}',
      transfer: 'Transferência entre contas registrada',
      advance: 'Registrado como adiantamento a fornecedor',
      controlException: 'Conciliado como exceção; aprovação avisada',
      application: 'Registrado como aplicação',
      cluster: 'Cliente {{document}} cadastrado, regra criada',
      installment: 'Recorrência criada',
      paidBill: 'Conta a pagar criada paga',
      fee: 'Tarifa registrada, regra criada',
      receipt: 'Recebimento avulso registrado',
      rejected: 'Sugestão recusada. O Alchemy não vai sugerir esse vínculo de novo.',
      ignored: 'Movimento ignorado',
      restored: 'Movimento de volta à fila',
      undone_one: 'Conciliação desfeita. O movimento voltou para a fila.',
      undone_other: '{{count}} conciliações desfeitas. Os movimentos voltaram para a fila.',
      failed: 'Não foi possível concluir a ação.',
      unbalanced: 'Ajuste os tipos até a diferença fechar.',
      selectCandidate: 'Selecione ao menos uma conta para conciliar.',
      created: {
        FEE_BILL: 'Tarifa criada e conciliada',
        VENDOR_ADVANCE_BILL: 'Adiantamento criado e conciliado',
        INTERNAL_TRANSFER: 'Transferência criada e conciliada',
        INTERNAL_OPERATION: 'Operação interna conciliada',
        HISTORICAL_LEDGER: 'Conciliado como já contabilizado no histórico importado',
      },
      openCreated: 'Abrir',
      reconcileFailed: 'Conciliação não concluída',
      undoneTitle: 'Conciliação desfeita',
      undoneBody: 'O movimento voltou para a fila.',
      batchTitle_one: '{{count}} conciliado',
      batchTitle_other: '{{count}} conciliados',
      batchLast: 'Último: {{name}} {{amount}}',
      dismiss: 'Fechar',
      /** ALC-4328 round 2 (O8): non-blocking notices on a saved pick. */
      notices: {
        RECON_V2_INTERNAL_OP_RESGATE_RAZAO_NO_CREDIT:
          'O Razão importado não creditou esta aplicação em {{date}} ({{money}}). O saldo de movimentos e o Razão vão divergir.',
      },
    },
    shortcutsToast:
      'J/K ou ↑/↓ navegam · Enter concilia · R recusa · I ignora · Z desfaz · / busca',
    shortcutsToastNoUndo: 'J/K ou ↑/↓ navegam · Enter concilia · R recusa · I ignora · / busca',
  },
  adjustmentTypes: {
    TARIFA: 'Tarifa bancária',
    JUROS: 'Juros',
    MULTA: 'Multa',
    DESCONTO: 'Desconto',
    IOF: 'IOF',
    IRRF: 'IRRF retido',
    INSS: 'INSS retido',
    ISS: 'ISS retido',
    FX: 'Variação cambial',
    ARREDONDAMENTO: 'Arredondamento',
    ADIANTAMENTO: 'Adiantamento',
    DEVOLUCAO_ADIANTAMENTO: 'Devolução de adiantamento',
    PIS: 'PIS retido',
    COFINS: 'COFINS retido',
    CSLL: 'CSLL retido',
    TARIFA_ADQUIRENCIA: 'Tarifa de adquirência',
    DESCONTO_INCONDICIONAL: 'Desconto incondicional (ajuste contábil)',
    CSRF: 'CSRF (PIS + COFINS + CSLL)',
  },
  log: {
    intro:
      'Tudo o que foi conciliado nesta conta em {{month}}, agrupado por quem decidiu. O que foi feito por regra pode ser desfeito item a item; desfazer devolve o movimento para a fila e estorna o lançamento.',
    columns: {
      who: 'Quem',
      what: 'O quê',
      items: 'Itens',
      amount: 'Valor',
      when: 'Quando',
    },
    who: {
      RULES: 'Regra',
      HISTORICAL: 'Histórico',
      AI: 'IA',
      AGENT: 'Agente',
      MANUAL: 'Você',
    },
    when: {
      ON_IMPORT: 'ao importar',
      BY_USER: 'por pessoa',
    },
    volumeSuffix: 'vol.',
    expand: 'Ver itens',
    collapse: 'Ocultar itens',
    more_one: 'e mais {{count}}',
    more_other: 'e mais {{count}}',
    undo: 'Desfazer',
    undoUnavailable:
      'Não é possível desfazer: um lançamento contábil desta conciliação não pode ser estornado automaticamente, ou um adiantamento ou reembolso gerado por ela já foi consumido.',
    undoBlocked: {
      MOVEMENT_JOURNAL_POSTED:
        'Não é possível desfazer: este rendimento ou imposto (IR/IOF) já foi contabilizado no próprio movimento bancário. Estorne primeiro esse lançamento no Razão e depois desfaça a conciliação.',
      GL_POSTING_NOT_REVERSIBLE:
        'Não é possível desfazer: um lançamento contábil desta conciliação não pode ser estornado automaticamente. Peça a um responsável para revisar os lançamentos.',
      PARTY_BALANCE_CONSUMED:
        'Não é possível desfazer: o adiantamento ou reembolso gerado por esta conciliação já foi consumido.',
      // ALC-4326 (S2-AC3): the investment-side movement an Aplicação/Resgate created.
      CREATED_LEG_LINKED_ELSEWHERE:
        'Não é possível desfazer: o movimento criado na conta de investimento já faz parte de outra conciliação. Desfaça aquela conciliação primeiro.',
      CREATED_LEG_PERIOD_CLOSED:
        'Não é possível desfazer: o movimento criado na conta de investimento está em um mês fechado. Reabra o mês primeiro.',
      // ALC-4363 r1: only a provider-removed item carries these two; the row names the month.
      STATEMENT_CLOSED:
        'Não é possível desfazer: o extrato do mês desta conciliação está fechado. Reabra o extrato primeiro.',
      ACCOUNTING_PERIOD_CLOSED:
        'Não é possível desfazer: o período contábil do lançamento está fechado. Reabra o período primeiro.',
    },
    empty: 'Nada conciliado ainda.',
  },
  undo: {
    progress_one: 'Conciliação desfeita. Falta {{count}} da mesma ação: confirme a próxima.',
    progress_other: 'Conciliação desfeita. Faltam {{count}} da mesma ação: confirme a próxima.',
    errors: {
      JUSTIFICATION_REQUIRED:
        'Informe uma justificativa: esta conciliação tem lançamentos contábeis que serão estornados.',
      REVERSAL_DATE_REQUIRED_FOR_BOOK:
        'Escolha o período do estorno para cada livro contábil com lançamentos desta conciliação.',
      REVERSAL_DATE_MISMATCH_FOR_SOURCE:
        'Os lançamentos de um mesmo movimento precisam ser estornados na mesma data. Escolha o mesmo período para todos os livros.',
      REVERSAL_BEFORE_ORIGINAL_PERIOD:
        'A data do estorno não pode ser anterior ao lançamento original. Escolha um período igual ou posterior.',
      NO_OPEN_PERIOD:
        'Não há período contábil aberto na data do estorno. Abra o período ou escolha outro antes de desfazer.',
      PAYMENT_CLEARING_REVERSAL_JOURNAL_MISSING:
        'O estorno da baixa do pagamento não foi gerado em um dos livros. Nada foi desfeito; peça a um responsável para revisar os lançamentos desta conciliação.',
      GROUP_HAS_LIVE_GL_POSTING:
        'Esta conciliação ainda tem um lançamento contábil ativo que não pode ser estornado automaticamente. Nada foi desfeito; peça a um responsável para revisar os lançamentos.',
      RECONCILIATION_UNDO_MOVEMENT_JOURNAL_POSTED:
        'Este rendimento ou imposto (IR/IOF) já foi contabilizado no próprio movimento bancário, e o desfazer não estorna esse lançamento. Estorne primeiro o lançamento do rendimento/imposto no Razão e depois desfaça a conciliação. Nada foi desfeito.',
      RECONCILIATION_UNDO_CREATED_BILL_NOT_DRAFT:
        'A conta criada nesta conciliação não é mais um rascunho (em aprovação, aprovada, contabilizada ou paga). Resolva-a em Contas a Pagar antes de desfazer.',
      GROUP_NOT_FOUND: 'Esta conciliação já foi desfeita em outra tela. A lista foi atualizada.',
      failed: 'Não foi possível desfazer a conciliação. Nada foi alterado.',
      // ALC-4747 (ADR 0031)
      RECONCILIATION_UNDO_MISMATCH_REMAINDER_IN_USE:
        'O saldo restante que esta conciliação agendou já foi enviado ao banco ou pago. Desfazer apagaria esse pagamento. Nada foi desfeito. Se outro movimento conciliou esse saldo, desfaça aquela conciliação primeiro. Se o saldo está no banco, mantenha esta conciliação.',
      RECONCILIATION_UNDO_MISMATCH_PAYMENT_NOT_RESTORABLE:
        'Esta conciliação substituiu um pagamento confirmado ou que estava no banco, ou a conta recebeu outro pagamento depois. Desfazer não consegue voltar a conta ao que era. Nada foi desfeito. Se você adicionou um pagamento depois, exclua-o e tente de novo. Se não, mantenha esta conciliação.',
      // ALC-4326 (S2-AC3)
      RECONCILIATION_UNDO_CREATED_LEG_LINKED_ELSEWHERE:
        'O movimento criado na conta de investimento por esta aplicação/resgate já faz parte de outra conciliação. Desfaça aquela conciliação primeiro. Nada foi desfeito.',
      // ALC-4326 round 1 (N1)
      RECONCILIATION_UNDO_INVESTMENT_GL_BELOW_ZERO:
        'Desfazer esta aplicação deixaria o saldo contábil da conta de investimento abaixo de zero, porque um resgate posterior ou um lançamento manual já tirou esse valor. Desfaça o resgate posterior ou estorne o lançamento manual primeiro. Nada foi desfeito.',
      // ALC-4328 round 1 (O2)
      RECONCILIATION_UNDO_LATER_RESGATE_POSTED:
        'Existe um resgate posterior ainda contabilizado nesta aplicação. Desfaça primeiro o resgate de {{laterDate}} nesta aplicação. Nada foi desfeito.',
      // ALC-4329 (O9)
      INVESTMENT_REVERSAL_BEFORE_POSTED_RESGATE:
        'Este estorno aumentaria o saldo contábil que o resgate de {{laterDate}} usou para separar principal e receita financeira. Desfaça primeiro o resgate de {{laterDate}} nesta aplicação. Nada foi desfeito.',
      // ALC-4363 r2 (ORCH Q16): a bank-removed line whose journal month is closed.
      RECONCILIATION_UNDO_PROVIDER_REMOVED_ACCOUNTING_PERIOD_CLOSED:
        'Reabra o período contábil de {{month}} de {{year}} para desfazer. O lançamento desta linha removida pelo banco está nesse período fechado. Nada foi desfeito.',
    },
  },
  ignored: {
    intro:
      'Movimentos ignorados não entram na prova de saldo e ficam listados no fechamento do mês como exceção.',
    columns: {
      date: 'Data',
      movement: 'Movimento',
      reason: 'Motivo',
      amount: 'Valor',
    },
    restore: 'Restaurar',
    empty: 'Nenhum movimento ignorado nesta conta.',
  },
};

export const reconciliationQueueEn = {
  browserTitle: 'Bank reconciliation v2',
  loading: 'Loading the queue…',
  paymentMethods: {
    PIX: 'PIX',
    TED: 'TED',
    DOC: 'DOC',
    BOLETO: 'Bank slip',
    CARD: 'Card',
    CARTAO: 'Card',
    DIRECT_DEBIT: 'Direct debit',
    DEBITO_AUTOMATICO: 'Direct debit',
    DIRECT_CREDIT: 'Direct credit',
    TRANSFERENCIA: 'Transfer',
    TARIFA: 'Fee',
    SPEI: 'SPEI',
  },
  loadError: 'Could not load the reconciliation.',
  writesComingSoon: 'Available soon',
  retry: 'Try again',
  header: {
    entity: 'Entity',
    kindBank: 'Bank account',
    kindCard: 'Card',
    kindSwitch: 'Switch between bank accounts and cards',
    prevPeriod: 'Previous month',
    nextPeriod: 'Next month',
    period: 'Period',
    periodMenu: 'Choose month',
    periodStatus: {
      CLOSED: 'Closed',
      OPEN_ITEMS_one: '{{count}} open',
      OPEN_ITEMS_other: '{{count}} open',
      CURRENT: 'Reconciling',
      IN_PROGRESS: 'In progress',
    },
    more: 'More actions',
    menu: {
      import: 'Import statement',
      acquirer: 'Acquirer report',
      rules: 'Reconciliation rules',
      export: 'Export reconciliation',
      shortcuts: 'Keyboard shortcuts',
      theme: 'Theme',
    },
    accountState: {
      CLOSED: '{{month}} closed',
      STALE_STATEMENT: 'Statement up to {{date}}',
      UP_TO_DATE: 'Up to date',
      TO_EXPLAIN_one: '{{count}} to explain',
      TO_EXPLAIN_other: '{{count}} to explain',
    },
    noBankAccounts: 'No bank accounts in this entity.',
    noCardAccounts: 'No cards in this entity.',
    comingSoon: 'Coming soon',
  },
  demo: {
    title: 'Demonstration data.',
    body: 'Nothing you do here is saved — the screen is wired to fictional data.',
  },
  errors: {
    RECON_V2_ACCOUNT_NOT_FOUND: 'Account not found.',
    RECON_V2_ENTITY_NOT_FOUND: 'Entity not found. Refresh the page and pick the entity again.',
    RECON_V2_PERIOD_INVALID: 'Enter the period as YYYY-MM, for example 2026-08.',
    RECON_V2_CARD_NOT_SUPPORTED: 'Cards have no balance proof yet.',
    RECON_V2_CLOSE_BLOCKED: 'The period still has open items and cannot be closed.',
    RECON_V2_GROUP_NOT_FOUND: 'Reconciliation not found — it may already have been undone.',
    RECON_V2_IGNORE_REASON_REQUIRED: 'Give a reason to ignore the movement.',
    RECON_V2_NOT_CONFIRMABLE: 'This movement is not in the Confirm group.',
    RECON_V2_NOTHING_TO_RECONCILE: 'Select at least one document or classify the amount.',
    RECON_V2_PERIOD_CLOSED: 'The period is closed. Reopen it before changing reconciliations.',
    RECON_V2_REOPEN_REASON_REQUIRED: 'Give a reason to reopen the period.',
    RECON_V2_GL_PERIOD_CLOSED:
      'This month accounting period is closed. Reopen it before reopening the statement.',
    RECON_V2_STATEMENT_ALREADY_CLOSED: 'This month is already closed.',
    RECON_V2_STATEMENT_CLOSED: 'The statement for this month is closed. Reopen it to make changes.',
    RECON_V2_STATEMENT_NOT_CLOSABLE:
      'This month cannot be closed yet: the balance proof still reports blockers.',
    RECON_V2_STATEMENT_NOT_CLOSED: 'The period is not closed.',
    RECON_V2_STATEMENT_NOT_FOUND: 'No statement found for this period.',
    RECON_V2_TRANSACTION_NOT_FOUND: 'Movement not found.',
    RECON_V2_TRANSACTION_NOT_IGNORED: 'This movement is not ignored.',
    RECON_V2_TRANSACTION_NOT_IN_QUEUE: 'This movement has already left the queue.',
    RECON_V2_UNBALANCED: 'Documents and adjustments do not add up to the movement.',
    /** The adjustment-posting refusals (ALC-4241), in the same order as the pt-BR block. */
    RECON_V2_ADJUSTMENT_UNSUPPORTED_COMBINATION:
      'This adjustment type is not supported on this side of the reconciliation yet.',
    RECON_V2_ADJUSTMENT_SIGN_INVALID:
      'The adjustment amount has the wrong sign for this adjustment type.',
    RECON_V2_ADJUSTMENT_ROUNDING_TOLERANCE_EXCEEDED:
      'The rounding difference exceeds the allowed tolerance. Review the allocation instead of treating it as rounding.',
    RECON_V2_ADJUSTMENT_JURISDICTION_MISMATCH:
      'This adjustment type exists only for Brazilian entities.',
    RECON_V2_ADJUSTMENT_NOTE_REQUIRED:
      'A withholding the source document did not declare needs a justification. Give the reason, or correct the document.',
    RECON_V2_ADJUSTMENT_CSRF_AMOUNT_INVALID:
      'The CSRF total could not be split into PIS, COFINS and CSLL.',
    RECON_V2_ADJUSTMENT_SLOT_NOT_RESOLVED:
      'The ledger account for this adjustment could not be resolved or created. Map it in the accounting settings.',
    RECON_V2_ADJUSTMENT_AGGREGATE_SPLIT_REQUIRED:
      'Give each tax separately; an undifferentiated total cannot be posted.',
    RECON_V2_ADJUSTMENT_ATTRIBUTION_REQUIRED:
      'Say which receivable this adjustment belongs to: the movement settles more than one.',
    RECON_V2_ADJUSTMENT_CASH_MISMATCH:
      'The documents and adjustments do not add up to the movement.',
    RECON_V2_ADJUSTMENT_SLOT_MAPPING_CONFLICT:
      'This adjustment account is already mapped to another ledger account. Review the mapping in the accounting settings.',
    RECON_V2_ADJUSTMENT_AMOUNT_UNREADABLE:
      'The amount stored on one of the adjustments could not be read. Correct it and reconcile again.',
    // ALC-4243 (S7) write refusals.
    RECON_V2_ADJUSTMENT_AP_FIX_ON_BILL:
      'This adjustment belongs on the bill: correct the withholding or the fee on the bill itself, not in the reconciliation.',
    RECON_V2_IDEMPOTENCY_KEY_REQUIRED:
      'The action was sent without a retry identifier. Refresh the page and try again.',
    RECON_V2_IDEMPOTENCY_KEY_REUSED:
      'This action was already sent with different data. Refresh the page and decide again.',
    RECON_V2_IDEMPOTENCY_IN_PROGRESS:
      'This action is still being processed. Wait a few seconds and try again.',
    RECON_V2_BATCH_TOO_LARGE: 'Confirm at most 100 movements at a time.',
    RECON_V2_BATCH_ITEM_NOT_CONFIRMABLE:
      'This movement is not in the Confirm group. Open it to decide.',
    RECON_V2_TRANSACTION_ALREADY_RECONCILED:
      'This movement is already reconciled. The queue has been refreshed.',
    RECON_V2_TREATMENT_UNSUPPORTED: 'This treatment is not supported yet.',
    RECON_V2_CREATE_KIND_UNSUPPORTED: 'This document type cannot be created here yet.',
    RECON_V2_RECONCILE_TARGETS_REQUIRED: 'Select at least one document or classify the amount.',
    RECON_V2_BATCH_ITEM_FAILED: 'This movement could not be confirmed. Open it to see why.',
    RECON_V2_TRANSACTION_ALREADY_IGNORED:
      'This movement is already ignored. Restore it before deciding.',
    RECON_V2_TARGET_TYPE_UNSUPPORTED: 'This document type cannot be reconciled here.',
    RECON_V2_CREATE_INPUT_INVALID:
      'Some data is missing to create the document. Check the vendor or the counterpart account.',
    RECON_V2_ADJUSTMENT_TYPE_UNSUPPORTED:
      'This adjustment type does not exist on this side of the reconciliation.',
    RECON_V2_ADJUSTMENT_ATTRIBUTION_UNKNOWN:
      'The adjustment points at a document this reconciliation does not settle.',
    RECON_V2_ADJUSTMENT_WITHOUT_TARGET:
      'Select the document the adjustments belong to before reconciling.',
    // ALC-4369: a retention needs a document that received cash.
    RECON_V2_ADJUSTMENT_TARGET_NO_CASH:
      'This adjustment is on a document that received 0.00 of this movement. Move the adjustment to a document that received cash, or remove it.',
    RECON_V2_ADJUSTMENT_EXCEEDS_ALLOCATION:
      'The retention or discount is as large as, or larger than, what is settled on this document, so no cash would be left. Attribute the adjustment to a document that received enough of this movement.',
    RECON_V2_GL_POSTING_FAILED:
      'The ledger posting was refused and the reconciliation was undone. Review the accounting setup and reconcile again.',
    RECON_V2_GL_POSTING_FAILED_DIRTY:
      'The ledger posting was refused and the reconciliation could not be undone. Do not repeat the action: ask an owner to review this movement.',
    RECON_V2_STATUS_CHANGED:
      'This movement changed while you were deciding. The queue has been refreshed: try again.',
    RECON_V2_BATCH_ITEM_AMBIGUOUS_ALLOCATION:
      'The suggestion does not add up to the movement. Open it to decide.',
    RECON_V2_TARGET_NOT_FOUND: 'Document not found. Refresh the list and select again.',
    RECON_V2_AR_SCHEDULE_ALLOCATION_INVALID:
      'The suggested instalments of this invoice are no longer open for these amounts. Open the movement to decide.',
    RECON_V2_AP_ADJUSTMENT_UNASSIGNED:
      'Say which payment this adjustment belongs to before reconciling.',
    RECON_V2_AP_SETTLEMENT_REJECTED:
      'The bill settlement refused this reconciliation. Review the bill and the amount.',
    RECON_V2_AMOUNT_EXACT_INVALID:
      'One of the amounts is invalid. Review them and reconcile again.',
    RECON_V2_CREATE_AND_RECONCILE_REFUSED:
      'The document could not be created and reconciled; nothing was saved. Review the data or pick another option.',
    RECON_V2_AP_SETTLEMENT_RETRY: 'The bill was being updated by another operation. Try again.',
    // ALC-4325 (S1): internal-operation pick pre-flight (design §13). Nothing is saved on these.
    RECON_V2_INTERNAL_OP_BOOK_NOT_SUPPORTED:
      'Internal operations are not available yet for books outside Brazil.',
    RECON_V2_INTERNAL_OP_DIRECTION_MISMATCH:
      'An application needs an outflow; a redemption needs an inflow.',
    // ALC-4358 (OQ7 A): "Final withholding tax" needs the tax account "IRRF Definitivo – Outras
    // Operações" mapped. Nothing is saved. `getReconV2ErrorPresentation` adds the link to the Fiscal tab.
    RECON_V2_INTERNAL_OP_IRRF_FINAL_SLOT_NOT_MAPPED:
      'The GL account “IRRF Definitivo – Outras Operações” is not mapped for this company. Map it in Settings → Accounting & Tax → Account mapping → Fiscal and try again. Nothing was saved.',
    RECON_V2_INTERNAL_OP_INVESTMENT_ACCOUNT_REQUIRED:
      'Choose the investment account the money goes to or comes from.',
    RECON_V2_INTERNAL_OP_COUNTERPART_INVALID:
      'The chosen account is not an active investment account of this company.',
    RECON_V2_INTERNAL_OP_INVESTMENT_ACCOUNT_GL_NOT_CONFIGURED:
      'The investment account has no GL account configured. Set it in Bank accounts → the account → GL account and try again.',
    RECON_V2_INTERNAL_OP_CURRENCY_MISMATCH: 'Both accounts must have the same confirmed currency.',
    RECON_V2_INTERNAL_OP_SWEEP_ACCOUNT_NEEDS_PAIR:
      "This account's balance includes the automatic investment. Choose the matching movement or an investment account.",
    /** GL_NOT_CONFIGURED when the refusal names the account (`details.bankAccountName`). */
    internalOpGlNotConfiguredNamed:
      'The investment account “{{bankAccountName}}” has no GL account configured. Set it in Bank accounts → {{bankAccountName}} → GL account and try again.',
    RECON_V2_HISTORICAL_LEDGER_PERIOD_DOCUMENT_BLOCKED:
      'This movement is before the end of the imported history. Use "Already booked in the imported history".',
    RECON_V2_HISTORICAL_LEDGER_OUTSIDE_PERIOD:
      'This movement is after the end of the imported history. Reconcile it normally.',
    RECON_V2_HISTORICAL_LEDGER_END_DATE_NOT_SET:
      'The entity has no imported history end date yet. Set it in Settings → Accounting & Tax → Entities → edit the entity → "Imported history end date".',
    RECON_V2_HISTORICAL_LEDGER_ACCOUNT_NOT_TIED_OUT:
      'This bank account was not checked when the end of the imported history was saved. Save the date again in Settings → Accounting & Tax → Entities → edit the entity → "Imported history end date", with this account tied out.',
    RECON_V2_INTERNAL_OP_USE_V2: 'Use the new reconciliation for applications and redemptions.',
    RECON_V2_TRANSFER_CURRENCY_MISMATCH: 'Both accounts must have the same confirmed currency.',
    RECON_V2_INTERNAL_OP_INVESTMENT_GL_BALANCE_NOT_POSITIVE:
      "This investment account's GL balance is zero or negative at the redemption's date. Book the account's opening balance before reconciling redemptions. Nothing was saved.",
    RECON_V2_INTERNAL_OP_PICK_REQUIRED:
      'Choose the type of internal operation (Application, Redemption, Yield, Withheld tax or Final withheld tax). Nothing was saved.',
    RECON_V2_INTERNAL_OP_RESGATE_OUT_OF_ORDER:
      'A later redemption on this investment account is already booked. Undo it before reconciling this redemption. Nothing was saved.',
    RECON_V2_INTERNAL_OP_RESGATE_IRRF_BLANK:
      'Part of this redemption is financial income and no withheld income tax (IRRF) was entered. Enter the IRRF the bank withheld, or confirm that none was withheld. Nothing was saved.',
    RECON_V2_INTERNAL_OP_APLICACAO_BEFORE_POSTED_RESGATE:
      'A redemption already booked with financial income on this investment used the balance of this date or a later one. Undo that redemption first, reconcile this application, then the redemption again. Nothing was saved.',
    RECON_V2_INTERNAL_OP_PAIR_AVAILABLE:
      'The matching movement is already on the investment account. Pair with it. Nothing was saved.',
    RECON_V2_INTERNAL_OP_ALREADY_RECLASSIFIED:
      'This movement was already booked from the checking account. Nothing was saved.',
    RECON_V2_INTERNAL_OP_ROUTE_A_MIRROR_MISSING:
      "We could not find this redemption on the investment account's statement. Import the investment account's statement or book it through accounting.",
    RECON_V2_INTERNAL_OP_SWEEP_CROSSES_MONTH:
      "The automatic investment pair crosses the end of the month. Book it as an Application and a Redemption on the automatic investment's investment account.",
    // ALC-4359 round 1: the account was deleted while this write waited. Nothing is saved.
    RECON_V2_BANK_ACCOUNT_DELETED:
      'This bank account was deleted before the reconciliation could run. Nothing was saved. Reload the screen.',
    // ALC-4235: the suggestions list is read in pages; both refusals are about the read only.
    RECON_V2_CANDIDATE_CURSOR_INVALID:
      'The suggestions list is out of date. Go back to the start of the list and try again.',
    RECON_V2_CANDIDATE_AMOUNT_RANGE_INVALID:
      'The amount filter is invalid: use values from zero up, and the minimum cannot be above the maximum.',
    /** RECON_V2_CREATE_INPUT_INVALID of an internal operation, by the field the API names. */
    createInputInvalidField: {
      operationType:
        'Choose the internal operation type (Application, Redemption, Yield, Withholding tax or Final withholding tax). Nothing was saved.',
      withheldTaxAmount:
        'Withheld tax applies only to a redemption and must be an amount of zero or more. Nothing was saved.',
      counterpartAccountId:
        'Choose the other statement movement or an investment account, not both. Nothing was saved.',
      sweepPartnerTransactionId:
        'The chosen movement cannot be the other half: it must be on the same account, with the opposite sign, the same amount, pending and within one business day. It only applies on an account whose statement balance includes the automatic investment. Nothing was saved.',
    },
    /** ALC-4327 (OQ9, option A): DIRECTION_MISMATCH by pick and account kind. */
    directionMismatch: {
      withholding: 'Withheld tax is always an outflow of the account. This movement is an inflow.',
      withholdingFinal:
        'Final withheld tax is always an outflow of the account. This movement is an inflow.',
      applicationChecking:
        'An Application is always an outflow of the account. This movement is an inflow: if it is a redemption, choose Redemption.',
      rescueChecking:
        'A Redemption is always an inflow to the account. This movement is an outflow: if it is an application, choose Application.',
      applicationInvestment:
        'On an investment account, an application is an inflow. This movement is an outflow: if it is a redemption, choose Redemption.',
      rescueInvestment:
        'On an investment account, a redemption is an outflow. This movement is an inflow: if it is an application, choose Application.',
      investmentPickNotValid:
        'On an investment account only Application (inflow) and Redemption (outflow) apply. Yield, Withheld tax and Final withheld tax are not booked from here.',
    },
    /** ALC-4327 (N2): INVESTMENT_GL_BALANCE_NOT_POSITIVE with the balance and the day it was read. */
    balanceNotPositive:
      'The ledger balance of this investment account is {{balance}} on {{date}}. Enter the opening balance before reconciling redemptions. Nothing was saved.',
    balanceNotPositiveNamed:
      'The ledger balance of {{account}} is {{balance}} on {{date}}. Enter the opening balance before reconciling redemptions. Nothing was saved.',
    // A day AFTER the redemption is below 0.00: the cause is a broken later ledger, not a missing
    // opening balance.
    balanceNotPositiveLater:
      'The ledger balance of this investment account is {{balance}} on {{date}}, after the date of this redemption. The account ledger is negative that day: review the entries of the account up to that date before reconciling. Nothing was saved.',
    balanceNotPositiveLaterNamed:
      'The ledger balance of {{account}} is {{balance}} on {{date}}, after the date of this redemption. The account ledger is negative that day: review the entries of the account up to that date before reconciling. Nothing was saved.',
    /** Refusals that name the day that decides them (ALC-4328 P6 / O7, ALC-4329 P5). */
    dated: {
      RECON_V2_INTERNAL_OP_RESGATE_OUT_OF_ORDER:
        'A redemption dated {{date}} is already posted on this investment. Undo it before reconciling this redemption. Nothing was saved.',
      RECON_V2_INTERNAL_OP_APLICACAO_BEFORE_POSTED_RESGATE:
        'The redemption dated {{date}}, already posted with financial income on this investment, used the balance of this date or a later one. Undo that redemption first, reconcile this application, then the redemption again. Nothing was saved.',
      RECON_V2_INTERNAL_OP_SWEEP_CROSSES_MONTH:
        "The automatic investment pair crosses the end of the month ({{lineDate}} and {{partnerDate}}). Book it as an Application and a Redemption on the automatic investment's investment account.",
    },
    /** ALC-4327 (BT-4): the system-account slots an internal operation can miss, in the user's words. */
    slotNames: {
      OP_INVESTMENT_YIELD_REVENUE: 'Financial investment yield',
      OP_INVESTMENT_IRRF_DEFINITIVE: 'Definitive IRRF on financial investments',
      IRRF_DEFINITIVO: 'Final IRRF, other operations (IRRF Definitivo – Outras Operações)',
      IRRF_COMPENSAR: 'IRRF to offset',
      OP_IN_TRANSIT: 'Cash in transit',
      OP_INVESTMENT_SHORT_TERM: 'Short-term investments',
      OP_INVESTMENT_LONG_TERM: 'Long-term investments',
    },
    slotNotMapped:
      'The GL account “{{slots}}” is not mapped for this company. Map it in Settings → Account mapping → Treasury and try again. Nothing was saved.',
    slotNotMappedUnnamed:
      'A system GL account is not mapped for this company. Map it in Settings → Account mapping → Treasury and try again. Nothing was saved.',
    // A tax slot (IRRF to offset) lives on the Fiscal tab, not on Treasury.
    slotNotMappedTax:
      'The GL account “{{slots}}” is not mapped for this company. Map it in Settings → Account mapping → Fiscal and try again. Nothing was saved.',
    slotNotMappedMixed:
      'The GL accounts “{{slots}}” are not mapped for this company. Map them in Settings → Account mapping → Treasury and Fiscal and try again. Nothing was saved.',
    openAccountMapping: 'Open account mapping',
    openTaxAccountMapping: 'Open tax account mapping',
    glReasons: {
      TAX_REGIME_NOT_CONFIGURED:
        "Set the entity's tax regime to post the come-cotas income tax. Nothing was saved.",
      CASH_MOVEMENT_CURRENCY_UNTRUSTED:
        "The account's currency is not confirmed. Confirm it in Settings → Accounts and reconcile again. Nothing was posted.",
      ZERO_OR_NEGATIVE_AMOUNT:
        'The movement amount is zero, so there is nothing to post. Review the statement line.',
      // ALC-4327 (Web-AC9): the other FAILED S0 / S0c reasons. Nothing was saved on any of them.
      CASH_MOVEMENT_REQUIRES_CONVERSION:
        "The bank account currency differs from the book's functional currency. Nothing was posted.",
      WITHHOLDING_JURISDICTION_UNSUPPORTED:
        'Withholding tax is only posted for companies in Brazil in this version.',
      INVESTMENT_YIELD_DEBIT_WITHHOLDING_UNSUPPORTED:
        'A yield reversal with withholding tax is not supported. Reconcile the tax separately.',
      HISTORICAL_LEDGER_SPLIT_TRANSFER_REQUIRES_ACCOUNTING:
        'One leg of this transfer is inside the imported history and the other has a fee or withholding. Post the other leg manually.',
      HISTORICAL_LEDGER_SPLIT_TRANSFER_TRANSIT_NOT_IN_RAZAO:
        'The imported history does not show how this transfer was booked. Check the imported history before posting.',
      INTERNAL_OP_HISTORY_SKIP_OUTSIDE:
        'The movement is no longer inside the imported history, so the investment/redemption cannot be reconciled now. Nothing was saved. Refresh the queue and check the imported history end date.',
      INTERNAL_OP_HISTORY_SKIP_NO_SELF_LINK:
        'The internal-operation reconciliation was not created, so nothing was saved. Reconcile again.',
      INTERNAL_OP_HISTORY_SKIP_KEPT_POSTED:
        'This movement already has a posted journal, so it was not treated as part of the imported history. Nothing was saved. Review the existing reconciliation.',
      RECONCILIATION_PAYMENT_MISMATCH:
        "The bank amount is smaller than the bill's payments. Edit the bill's payments to match the bank amount and reconcile again. Nothing was saved.",
      RECONCILIATION_PAYMENT_MISMATCH_STALE:
        'The bill changed after you opened this screen. Close and open the reconciliation again.',
      RECONCILIATION_PAYMENT_MISMATCH_JUSTIFICATION_REQUIRED:
        "This bill has a payment the bank confirmed with a different amount. Edit the bill's payments to match the bank amount and reconcile again. Nothing was saved.",
      RECONCILIATION_PAYMENT_MISMATCH_BANK_CANCELLATION_REQUIRED:
        "This bill has a payment sent to the bank with a different amount. Cancel it at the bank. Then edit the bill's payments to match the bank amount and reconcile again. Nothing was saved.",
      RECONCILIATION_PAYMENT_MISMATCH_REMAINDER_INVALID:
        "An approved bill must schedule the rest as a new payment. Edit the bill's payments to match the bank amount and reconcile again. Nothing was saved.",
      RECONCILIATION_PAYMENT_MISMATCH_PAYMENT_POSTED:
        "This payment already has journal entries and cannot be replaced here. Edit the bill's payments to match the bank amount and reconcile again. Nothing was saved.",
      RECONCILIATION_PAYMENT_MISMATCH_SEND_IN_FLIGHT:
        'This payment is being sent to the bank right now. Wait until the send finishes, then reconcile again. Nothing was saved.',
      RECONCILIATION_PAYMENT_MISMATCH_PERMISSION_REQUIRED:
        'To replace a payment that is confirmed or held by the bank, you need the permission to cancel Accounts Payable payments. Ask someone who has it to finish this reconciliation. Nothing was saved.',
      HISTORICAL_LEDGER_SPLIT_TRANSFER_TRANSIT_ALREADY_POSTED:
        'The imported history already booked this transfer directly, and the other leg was already posted through the transit account. Undo this transfer reconciliation, which reverses the transit entry, then pair the two movements again.',
      INVESTMENT_RESGATE_RETRY_REFUSED:
        'This is an investment redemption, and it cannot be reposted by a retry. Undo the reconciliation and pick Resgate again. Nothing was posted.',
      CROSS_DAY_INVESTMENT_REDEMPTION_REQUIRES_ACCOUNTING:
        'A redemption is reconciled only with the investment movement of the same day. Wait for that movement. Nothing was saved.',
      OP_IN_TRANSIT_MISSING:
        "The investment's movement has not arrived yet, and the in-transit cash account is not set up. Set it up in the chart of accounts or wait for the same-day movement. Nothing was saved.",
      TRANSFER_SAME_ACCOUNT:
        'Both movements are on the same account. A transfer needs two accounts; for an automatic sweep on the same account, pick the application or the redemption and name the other movement. Nothing was saved.',
      // ALC-4359 round 1: the post-commit posting found the account deleted. Nothing was saved.
      RECON_V2_BANK_ACCOUNT_DELETED:
        'This bank account was deleted before the entry could be posted. Nothing was saved. Reload the screen.',
      // ALC-4235 (Charlie ACC-09): undone before the journal was recorded; the journal rolled back.
      MOVEMENT_POSTING_ROW_NO_SELF_LINK:
        'The reconciliation of this internal operation no longer exists, so the journal entry was not posted. Reload the queue and reconcile again.',
      // ALC-4235 (ccy-s3-ar): every AR receipt refusal, from the one registry in @alinea/types.
      ...AR_RECEIPT_REFUSAL_GL_REASONS_EN,
    },
    openTaxRegimeSettings: 'Set tax regime',
    // ALC-4327: the GL-binding refusal links to the investment account's GL field.
    openInvestmentAccountGlSettings: 'Set GL account',
    /** No coded answer: the connection dropped or a gateway answered 502/504. */
    unreachable:
      'The server answer could not be confirmed. Try again: the same action is re-sent and does not post twice.',
  },
  proof: {
    noStatement:
      'There is no statement for this month. Import a statement before closing the period.',
    statement: 'Statement on {{date}}',
    statementStale: 'Statement',
    ledger: 'Ledger',
    ledgerAccount: 'Ledger {{code}}',
    difference: 'Difference',
    staleBanner: 'Statement stopped on {{date}}. Refresh it to close {{month}}.',
    typeBalance: 'Type balance',
    /** Keyed by `ReconV2BalanceProof.unavailableReason` (ALC-4237). */
    unavailableReason: {
      CARD_NOT_SUPPORTED: 'The balance proof is not available for cards yet.',
      LEDGER_ACCOUNT_RESTRICTED:
        'This bank account posts to a restricted GL account you do not have access to.',
      LEDGER_SHARED_GL_ACCOUNT:
        'More than one bank account posts to the same GL account, so there is no per-account ledger balance.',
      LEDGER_PERIOD_NOT_FOUND:
        'No accounting period contains this month end, so there is no ledger balance to compare against.',
      LEDGER_PERIOD_NOT_MONTHLY:
        'This accounting calendar is not monthly, so the ledger balance does not correspond to a single month.',
    },
    /** Which source stated the statement balance (`statement.source`). */
    statementSource: {
      USER_DECLARED: 'Typed statement balance',
      STATEMENT_CLOSING: 'Statement closing balance',
      DAILY_BALANCE: 'Daily account balance',
      LAST_TRANSACTION_BALANCE: 'Balance after the last movement',
    },
    /** A statement balance a person typed (`statement.source = USER_DECLARED`, ALC-4360). */
    typed: {
      label: 'Typed balance on {{date}}',
      closeDescription:
        'The typed balance and the ledger match on {{date}}. Closing locks the period on this account: new movements dated {{month}} will require reopening.',
      closeNote:
        'This balance was typed by {{user}} on {{date}}; it did not come from a statement.',
      closeNoteNoUser: 'This balance was typed on {{date}}; it did not come from a statement.',
      closeNoteBare: 'This balance was typed; it did not come from a statement.',
    },
    /**
     * The newest known balance of an account whose month-end statement is stopped or missing
     * (`lastAvailable`, ALC-4370). Every figure carries its own date; none is the month-end proof.
     */
    lastAvailable: {
      bank: 'Bank on {{date}}',
      ledger: 'Ledger on {{date}}',
      difference: 'Difference on {{date}}',
    },
    /** `pendingBreakdown`: the queue reaches past this month (decision D-S2b). */
    pending: {
      thisPeriod: 'Queued in this period',
      earlierPeriods: 'Queued from earlier months',
    },
    unavailableTitle: 'Balance proof not available for cards',
    unavailableBody:
      'Proof by card statement ships in a later delivery. The card decision queue works as usual.',
    progress: '{{reconciled}} of {{total}} reconciled, {{auto}} by Alchemy',
    balanced: 'Statement and ledger match',
    viewLog: 'View log',
    fxLine: '{{amount}} of unremeasured FX variation',
    fxHint: 'Blocks closing until remeasurement runs.',
    residualLine: '{{amount}} in the ledger with no statement movement',
    residualHint: 'Blocks closing until explained.',
    conversion: '{{from}} → {{to}} at {{rate}} · {{source}} of {{date}}',
    reconnect: 'Reconnect',
    importOfx: 'Import OFX',
    close: 'Close {{month}}',
    closedPill: '{{month}} closed',
    closedAt: 'Closed on {{date}}',
    reopen: 'Reopen',
    blockersTitle: 'What is missing to close',
    blockers: {
      QUEUE_NOT_EMPTY: 'There are still movements to explain',
      DIFFERENCE_NOT_ZERO: 'The difference is not zero yet',
      UNEXPLAINED_RESIDUAL: 'There is ledger value with no statement movement',
      UNREMEASURED_FX: 'Unremeasured FX variation',
      STATEMENT_BALANCE_MISSING: 'No statement balance at month end',
      BANK_GL_ACCOUNT_UNMAPPED: 'Bank GL account not mapped',
      FX_RATE_MISSING: 'No exchange rate for month end',
      ALREADY_CLOSED: 'Period already closed',
      PERIOD_IN_PROGRESS: 'The month has not ended yet',
      STATEMENT_STALE:
        'The statement is out of date. Refresh the connection or import the statement before closing.',
      PROVIDER_REMOVED_ACTIVE_GL_one:
        'The bank removed {{count}} line that still has an active journal entry. Resolve it before closing.',
      PROVIDER_REMOVED_ACTIVE_GL_other:
        'The bank removed {{count}} lines that still have an active journal entry. Resolve them before closing.',
    },
    closeDialog: {
      title: 'Close {{month}} for {{account}}?',
      description:
        'Statement and ledger match on {{date}}. Closing locks the period on this account: new movements dated {{month}} will require reopening.',
      confirm: 'Close {{month}}',
    },
    reopenDialog: {
      title: 'Reopen {{month}} for {{account}}?',
      description: 'Reopening is recorded in the audit trail. Enter the reason.',
      reasonLabel: 'Reopen reason',
      reasonPlaceholder: 'E.g. supplementary statement received on 09/03',
      confirm: 'Reopen',
    },
    toasts: {
      closed: '{{month}} closed for {{account}}',
      reopened: '{{month}} reopened for {{account}}',
      closeFailed: 'Could not close the period.',
      reopenFailed: 'Could not reopen the period.',
    },
  },
  /**
   * Bank lines the provider removed but the ERP kept because they carry active posted GL
   * (ALC-4363, decision ORCH Q11 = A): reconciling items, outside the decision groups.
   */
  removed: {
    title: 'Removed by the bank',
    subtitle: 'No statement line, still in the ledger',
    removedAt: 'Removed on {{date}}',
    /** Tag on an item that belongs to another month than the one on screen (ORCH Q15). */
    fromMonth: 'Posted in {{month}} {{year}}',
    undo: 'Undo reconciliation',
    /** Visible row text, no button: the active GL is a movement journal no reconciliation holds. */
    noGroup: 'No reconciliation to undo. Reverse the journal entry in Accounting.',
    /** Visible row text, no button: the undo would be refused until the month is reopened. */
    undoBlocked: {
      STATEMENT_CLOSED: 'Reopen the {{month}} statement to undo.',
      ACCOUNTING_PERIOD_CLOSED: 'Reopen the {{month}} accounting period to undo.',
      // The closed month is not the month on screen: it names its year too.
      STATEMENT_CLOSED_withYear: 'Reopen the {{month}} {{year}} statement to undo.',
      ACCOUNTING_PERIOD_CLOSED_withYear: 'Reopen the {{month}} {{year}} accounting period to undo.',
    },
  },
  tabs: {
    toExplain: 'To explain',
    reconciled: 'Reconciled',
    ignored: 'Ignored',
  },
  search: {
    placeholder: 'Name, tax id or amount',
    label: 'Search the queue',
    clear: 'Clear search',
    filters: 'Filters',
    direction: 'Direction',
    both: 'In and out',
    debitOnly: 'Out only',
    creditOnly: 'In only',
  },
  groups: {
    CONFIRM: 'Confirm',
    RESOLVE_DIFF: 'Resolve difference',
    REVIEW: 'Review',
    IDENTIFY: 'No document',
    sectionTitle: {
      CONFIRM: 'Confirm',
      RESOLVE_DIFF: 'Resolve difference',
      REVIEW: 'Review before reconciling',
      IDENTIFY: 'No document',
    },
    sectionSubtitle: {
      CONFIRM: 'Counterparty, amount and date match.',
      RESOLVE_DIFF: 'Right document, different amount. Say what the difference is.',
      REVIEW: 'Matches, but has an accounting or control effect.',
      IDENTIFY: 'No matching document.',
    },
    batch: 'Reconcile {{count}}',
    /** Above RECON_V2_BATCH_CONFIRM_MAX: one batch sends the first {{count}} of {{total}}. */
    batchCapped: 'Reconcile {{count}} of {{total}}',
    batchDialog: {
      title: 'Reconcile {{count}} movements at once?',
      titleCapped: 'Reconcile {{count}} of {{total}} movements now?',
      rest_one: 'The remaining movement stays in the queue for the next round.',
      rest_other: 'The other {{count}} movements stay in the queue for the next round.',
      description:
        'Each movement becomes its own reconciliation with the suggested document. Journals are posted now and can be undone item by item in the log.',
      confirm: 'Reconcile {{count}}',
    },
    batchToast_one: '{{count}} movement reconciled',
    batchToast_other: '{{count}} movements reconciled',
    batchPartial: '{{ok}} reconciled, {{failed}} failed',
  },
  row: {
    delta: 'Δ {{amount}}',
    select: 'Select {{name}}',
  },
  empty: {
    entities: 'No entities available for reconciliation.',
    accounts: 'No accounts available for this entity and period.',
    groupTitle: 'Nothing in {{group}}',
    groupBody: 'There are still items in {{other}}.',
    goTo: 'Go to {{other}}',
    seeAll: 'See all',
    searchTitle: 'Nothing found for “{{q}}”',
    searchBody: 'Clear the search to see the whole queue.',
    staleTitle: 'No movements to explain',
    staleBody:
      'The {{bank}} statement stopped on {{date}}. Reconnect or import the OFX to continue.',
    queueTitle: 'Queue empty',
    queueBody: 'Every {{month}} movement is explained.',
    /** The queue has no decision left, but removed items still block the close (ORCH Q15). */
    removedOnlyTitle: 'No movement to decide',
    removedOnlyBody:
      'Only the lines removed by the bank are left, above. Resolve each one to close.',
    closedTitle: '{{month}} closed',
    closedBody: 'New movements dated {{month}} will require reopening the period.',
    readyTitle: 'Nothing to explain',
    readyBody:
      'Statement and ledger match on {{date}}. Close {{month}} on this account to lock the period.',
    detailTitle: 'No item selected',
    detailBody: 'Pick a status with items in the list, or use keys 1–5.',
  },
  decision: {
    raw: 'On the statement',
    why: 'Why Alchemy suggests this',
    source: {
      RULES: 'Rule',
      HISTORICAL: 'History',
      AI: 'AI',
      MANUAL: 'Manual',
    },
    criteriaTitle: '{{matched}} of {{total}} criteria match',
    criteria: {
      COUNTERPARTY: 'Counterparty',
      AMOUNT: 'Amount',
      DATE: 'Date',
      REFERENCE: 'Reference',
      METHOD: 'Method',
    },
    criteriaState: {
      ok: 'matches',
      delta: 'difference',
      fail: 'no match',
    },
    open: 'Open',
    document: 'Document',
    suggested: 'Suggested link',
    columns: {
      bank: 'On the statement',
      doc: 'In Alchemy',
    },
    // ALC-4364: justification and document of each adjustment (same rule as the server pre-flight).
    adjustmentFields: {
      note: 'Justification',
      notePlaceholder: 'E.g. the customer withheld the tax and will issue the certificate',
      optional: 'optional',
      required: 'required',
      requiredMasculine: 'required',
      noteRequiredHint:
        'Explain why the customer withheld: the withholding is not yet declared on the document.',
      document: 'Document',
      documentPlaceholder: 'Choose the document',
      documentRequiredHint: 'This adjustment applies to a single document. Choose which one.',
      blockedToast:
        'An adjustment is missing its justification or its document. Fill in the highlighted fields.',
    },
    // ALC-4364: settle the whole document when a withholding closes it without the bank covering it.
    allocation: {
      settleFull: 'Settle the whole document',
      settleFullHint:
        'Settles the open amount; the difference from the bank becomes an adjustment (for example, a withholding).',
      noCash:
        "The movement has no amount left for this document: it stays out of this reconciliation. Deselect it or, if another document has 'Settle the whole document' ticked, untick that option to free up amount.",
    },
    diff: {
      title: 'What the difference is',
      hint: 'Change the type if it is wrong.',
      addRow: 'Add adjustment',
      removeRow: 'Remove adjustment',
      type: 'Adjustment type',
      amount: 'Amount',
      csrfShortcut: 'CSRF (PIS + COFINS + CSLL)',
      basis: 'Basis',
      account: '{{side}} on {{code}} {{name}}',
      debit: 'Debit',
      credit: 'Credit',
      explained: 'Difference of {{amount}} fully explained',
      notComparable:
        'No measured difference: the suggestion carries an entry this screen does not show',
      unexplainedMore: 'Does not close: {{amount}} more in the bank with these types',
      unexplainedLess: 'Does not close: {{amount}} less in the bank with these types',
      adjustUntilZero: 'Adjust the types until the difference closes.',
      discountKind: 'Discount kind',
      discountConditional: 'Conditional discount',
      discountUnconditional: 'Unconditional discount (revenue deduction)',
    },
    review: {
      title: 'Control signal',
      whatHappens: 'What happens when you reconcile',
      signals: {
        PAYMENT_BEFORE_INVOICE: 'Invoice issued after the payment',
        PAYMENT_BEFORE_APPROVAL: 'Bill still under review',
        DIRECTION_MISMATCH: 'Movement sign does not match the description',
        POSSIBLE_DUPLICATE: 'Possible duplicate payment',
      },
      effects: {
        PAYMENT_BEFORE_INVOICE:
          'The amount stays in vendor advances until the invoice is posted. Once the bill exists, the advance is settled against it.',
        PAYMENT_BEFORE_APPROVAL:
          'The reconciliation is flagged as a control exception and the bill approver is notified to finish the review. The journal is posted only after approval.',
        DIRECTION_MISMATCH:
          'Registering as an application posts the outflow against the investment account. Sending for correction returns the movement to the import with no journal.',
        POSSIBLE_DUPLICATE:
          'Reconciling anyway records the exception in the audit trail for finance review.',
      },
      treatment: {
        SETTLEMENT: 'Bill settlement',
        ADVANCE: 'Vendor advance',
        INTERNAL_APPLICATION: 'Financial application',
      },
      actions: {
        registerAdvance: 'Register advance',
        settleAnyway: 'Reconcile as settlement anyway',
        settleAndNotify: 'Reconcile and notify approval',
        openBill: 'Open bill',
        registerApplication: 'Register application',
        settleDuplicate: 'Reconcile anyway',
      },
      /** Outside the demo: the effect of the only treatment the API books (S7: SETTLEMENT). */
      effectsLive: {
        PAYMENT_BEFORE_INVOICE:
          'Recording it as an advance is coming soon. Reconciling as a settlement pays the bill with this payment, even though the invoice came later.',
        DIRECTION_MISMATCH:
          'Recording it as a financial application is coming soon; nothing is posted from this suggestion.',
      },
      noTreatment:
        'To move on now, reject the suggestion (R): the movement goes to No document, where you pick another document or create one (Create entry). Or ignore the movement (I).',
      noTreatmentNoCandidate:
        'To move on now, ignore the movement (I) or wait for a new suggestion.',
      anomaly: {
        question: 'What happened?',
        application: 'It was an application',
        importError: 'Statement import error',
      },
    },
    identify: {
      needsTarget: {
        title: 'Pick a document to reconcile with these adjustments.',
        debit:
          'A classification alone does not reconcile the movement. If it is a fee with no document, create the bank fee: the bill is created and reconciled in one step.',
        credit:
          'A classification alone does not reconcile the movement. Select the receivable or create the document from the Create menu.',
      },
      title: 'Suggestions',
      diff: 'diff. {{amount}}',
      openCount_one: '{{count}} open',
      openCount_other: '{{count}} open',
      // Second line of a suggestion: "Payable 5580455005 · Comp. 09/2026 · Due 10/10/2026".
      // A part the document does not have is left out, never shown as a dash.
      competence: 'Comp. {{date}}',
      dueOn: 'Due {{date}}',
      loadingMore: 'Loading more…',
      loadMoreError: 'Could not load more suggestions.',
      loadMoreRetry: 'Try again',
      amountFilter: {
        label: 'Open amount',
        min: 'Minimum amount',
        max: 'Maximum amount',
        minHandle: 'Minimum amount slider',
        maxHandle: 'Maximum amount slider',
        clear: 'Clear',
        invalid: 'The minimum amount cannot be above the maximum.',
        orMore: '{{amount}}+',
        valueOrMore: '{{amount}} or more',
        valueNoMinimum: 'no minimum',
      },
      searchPlaceholder: 'Vendor, document number or amount',
      loadingCandidates: 'Loading open documents…',
      type: 'Type',
      typeAP: 'Payable',
      typeAR: 'Receivable',
      typeTransfer: 'Transfers',
      typeAll: 'All types',
      emptyFilter: 'No open documents in this filter. Use Create to register this movement.',
      emptySearch: 'No open documents for “{{q}}”. Use Create to register this movement.',
      hints: {
        TRANSFER_PAIR:
          'Same amount and same day in {{account}}, same company. Select it to register both sides as a transfer.',
        CNPJ_CLUSTER: '{{why}}',
        INSTALLMENT: '{{why}}',
        FEE_LIKE:
          'Looks like a bank charge. Register it as a fee: the bill is created and reconciled in one step.',
        weak: 'The previous suggestion matched {{matched}} of {{total}} criteria and was discarded.',
        rejected: 'You rejected the suggestion. Pick another document or create a new one.',
        nomatch: 'No document with this amount and counterparty. Showing the closest ones.',
      },
      hintActions: {
        TRANSFER_PAIR: 'Register transfer',
        CNPJ_CLUSTER: 'Create customer and rule',
        INSTALLMENT: 'Create recurrence',
        FEE_LIKE: 'Register fee',
      },
      exact: 'Exact amount',
      discarded: 'Discarded suggestion',
      rejectedTag: 'Rejected by you',
      dueSameDay: 'Due on the same day',
      dueAfter_one: 'Due in {{count}} day',
      dueAfter_other: 'Due in {{count}} days',
      dueBefore_one: 'Overdue by {{count}} day',
      dueBefore_other: 'Overdue by {{count}} days',
      sameDay: 'Same day',
      partial: 'Partial payment',
      partialApplies: 'applies {{applied}}, {{remaining}} stays open',
      partialFull: 'covers the full amount',
      exceeds: 'The transaction exceeds the documents by {{amount}}. How to classify?',
      accountsExceed: 'The documents exceed the transaction by {{amount}}. How to classify?',
      classified: 'Difference of {{amount}} classified',
      discardedTitle: 'Discarded suggestions',
      discardedReason: {
        LOW_CRITERIA: 'Few criteria match',
        USER_REJECTED: 'Rejected by you',
      },
      create: {
        comingSoonHint: 'Coming soon: for now, select documents or classify the amount.',
        cluster: 'Customer and rule for this tax id',
        installment: 'Recurrence (installments {{from}} to {{to}})',
        transfer: 'Transfer between accounts',
        paidBill: 'Bill already paid',
        fee: 'Fee, IOF or charge',
        receipt: 'One-off receipt',
        internalOp: 'Reconcile as internal operation',
        vendorAdvance: 'Vendor advance (no bill)',
      },
      createDialog: {
        confirm: 'Create and reconcile',
        cancel: 'Cancel',
        vendor: 'Vendor',
        vendorOptional: 'Vendor (optional)',
        vendorPlaceholder: 'Search vendor',
        vendorDefaultHint: 'With no vendor, the fee goes to the default bank-fee vendor.',
        counterpart: 'Counterpart account',
        counterpartPlaceholder: 'Select the other account',
        noCounterpart: 'This entity has no other account to hold the other side of the transfer.',
        descriptionLabel: 'Description (optional)',
        descriptionPlaceholder: 'Shown on the created document',
        FEE_BILL: {
          title: 'Create fee and reconcile',
          description:
            'Creates a {{amount}} bank-fee bill and reconciles it with “{{description}}”.',
        },
        VENDOR_ADVANCE_BILL: {
          title: 'Create advance and reconcile',
          description:
            'Creates a {{amount}} vendor advance with no originating bill and reconciles it with “{{description}}”.',
        },
        INTERNAL_TRANSFER: {
          title: 'Create transfer and reconcile',
          description:
            'Creates the other side of the {{amount}} transfer on the chosen account and reconciles both sides.',
        },
        INTERNAL_OPERATION: {
          title: 'Reconcile as internal operation',
          description:
            'Records “{{description}}” ({{amount}}) as an internal operation. Choose the type and, for an application or redemption, the investment account.',
        },
        // ALC-4327 (Web-AC1..AC6): the internal-operation picker.
        internalOp: {
          typeLabel: 'Operation type',
          types: {
            YIELD: 'Yield',
            APPLICATION: 'Application',
            RESCUE: 'Redemption',
            WITHHOLDING_TAX: 'Withholding tax',
            WITHHOLDING_TAX_FINAL: 'Final withholding tax',
          },
          // ALC-4358 (OQ7 A, AC5): a suggestion only, never a pre-selection.
          finalWithholdingHint:
            'This movement looks like an income tax that is not on a financial investment (for example, on a foreign-exchange operation). If it was withheld as a final tax and cannot be offset, choose “Final withholding tax”.',
          bankClassification: 'Bank classification: {{type}}',
          investmentAccount: 'Investment account',
          investmentAccountPlaceholder: 'Select the investment account',
          noInvestmentAccount: 'This company has no active investment account yet.',
          investmentCurrencyNotSupported:
            'In {{currency}}: internal operations use BRL accounts only in this version.',
          createInvestmentAccount: 'Create investment account',
          createAccountFailed: 'The investment account could not be created. Nothing was saved.',
          createAccountErrors: {
            BANK_ACCOUNT_COA_ASSIGNMENT_REQUIRED:
              'The company has no active chart of accounts yet. Set one up before creating the account. Nothing was saved.',
            BANK_ACCOUNT_GL_OFFSET_INVALID:
              'The chosen GL account cannot be used for an investment account. Nothing was saved.',
            INVALID_CURRENCY_CODE: 'The chosen currency is not valid. Nothing was saved.',
            FUNCTIONAL_CURRENCY_MISMATCH:
              "The account's currency must be the company's functional currency. Nothing was saved.",
            FUNCTIONAL_CURRENCY_UNRESOLVED:
              'The company has no functional currency yet. Set it before creating the account. Nothing was saved.',
            FORBIDDEN: 'You do not have permission to create bank accounts. Nothing was saved.',
          },
          createdAccountNotListed:
            'The account was created, but only active BRL investment accounts of this company can be chosen here.',
          openGlSettings: 'Set GL account',
          // BT-1: the route text follows the server's route read (route + pairing), never a guess.
          route: {
            checking: 'Checking the reconciliation route…',
            checkFailed: 'The route of this reconciliation could not be checked with the server.',
            created: 'A {{amount}} movement will be created on {{account}}',
            mirror:
              'Will be paired with the matching movement on {{account}}. No new movement is created.',
            mirrorSameDay:
              'Will be paired with the {{money}} movement already imported on {{account}}, from the same day. No new movement is created.',
            awaiting:
              "The investment movement has not reached {{account}}'s statement yet. The {{money}} leaves the account and waits in cash in transit until it is imported.",
            completesAwaiting:
              'Completes an application that was waiting for its other side: the {{money}} leaves cash in transit and enters {{account}}.',
            // The same pairing seen from the other side of the pair: a line on the investment
            // account that opens the wait, and a line on the checking account that completes it.
            awaitingInvestmentSide:
              'The checking-account movement has not reached the statement yet. The {{money}} enters {{account}}; the other side waits in cash in transit until it is imported.',
            completesAwaitingCheckingSide:
              'Completes an application that was waiting for its other side: the {{money}} leaves the account and clears cash in transit.',
            sweep:
              'Paired with the other half of the same statement. No journal entry: the statement balance already includes both.',
          },
          // ALC-4327 (Web-AC6): the other half of a sweep line.
          sweep: {
            label: 'Other half of the automatic investment',
            hint: "This account's balance already includes the automatic investment: choose the other movement of the same statement or an investment account.",
            modePartner: 'Paired statement movement',
            modeInvestment: 'Investment account',
            partnerLabel: 'Paired movement',
            partnerPlaceholder: 'Select the paired movement',
            partnerLoading: 'Looking for the paired movement…',
            partnerLoadFailed: 'Could not look for the paired movement.',
            partnerNone:
              'No paired movement found: it must be on the same account, with the opposite sign, the same amount, pending and within one business day.',
            partnerTruncated:
              "The month's movement list is incomplete. If the pair is not listed, filter the queue by amount and try again.",
          },
          noJournal: 'No journal entry',
          noJournalHint:
            'The movement is inside the imported history; the imported ledger already booked it.',
          loadingAccounts: 'Loading accounts…',
          accountsLoadFailed: "This company's bank accounts could not be loaded.",
          retry: 'Try again',
          // ALC-4327 (Web-AC5, BT-2): the redemption split and the tax the bank withheld.
          irrf: {
            label: "Tax withheld (from the bank's income report)",
            hint: "The income tax (IRRF) the bank withheld on this redemption, as shown on the bank's income report. Left blank, the system warns before posting with no tax when there is financial income.",
            warningTitle: 'Part of this redemption is financial income',
            warningBody:
              "The {{amount}} redemption is above the investment's book amount: {{excess}} is financial income and no withheld tax was entered. Enter the tax the bank withheld or confirm there was none. Nothing was saved.",
            // Once a tax amount is typed the warning no longer says none was entered.
            warningBodyTyped:
              "The {{amount}} redemption is above the investment's book amount: {{excess}} is yield. The tax entered is {{tax}}. Nothing was saved.",
            principal: 'Principal redeemed from the investment',
            income: 'Financial income',
            tax: 'Tax withheld',
            incomeBreakdown: 'The income adds the {{excess}} yield and the {{tax}} tax.',
            consent: 'I confirm the bank withheld no tax on this redemption (tax 0.00)',
          },
        },
      },
      backToSuggestions: '← Back to suggestions',
      // ALC-4327 (Web-AC7): a line dated on or before the entity's "Fim do histórico importado".
      historical: {
        action: 'Already booked in the imported history',
        hintNoDate:
          'Movement of {{date}}, inside the imported history. The imported ledger already booked it: reconcile without a document.',
        internalOpHint:
          'Use “Internal operation” to also record the movement on the investment account.',
        noJournal: 'No journal entry',
        hiddenCreate:
          'Fee and advance bills are not created for movements inside the imported history: the imported ledger already booked them.',
      },
    },
    footer: {
      transaction: 'Transaction',
      accounts: 'Documents',
      adjustments: 'Adjustments',
      difference: 'Difference',
      reconcile: 'Reconcile',
      reconcileWithAdjustments: 'Reconcile with adjustments',
      reconcileTransfer: 'Register transfer',
      reconcileBatchHint: 'Enter',
      reject: 'Not this one',
      rejectHint: 'R',
      ignore: 'Ignore',
      undo: 'Undo',
      ignoreHint: 'I',
      create: 'Create entry',
      navigate: 'navigate',
      search: 'search',
      /** Collapsed shortcut sheet when the decision footer is under ~900px. */
      shortcuts: 'Shortcuts',
      pairWith: 'Pair: {{account}} · {{date}} · {{amount}}',
    },
    ignoreDialog: {
      title: 'Ignore this movement?',
      description:
        'Ignored movements are excluded from the balance proof and listed in the month close as exceptions. The reason is required.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'E.g. duplicate bank reversal, already handled in July',
      confirm: 'Ignore',
    },
    undoDialog: {
      nothing: 'Nothing to undo in this session.',
    },
    toasts: {
      reconciled: 'Reconciled with {{ref}}',
      reconciledWithAdjustments: 'Reconciled with {{ref}} and {{adjustments}}',
      transfer: 'Transfer between accounts registered',
      advance: 'Registered as vendor advance',
      controlException: 'Reconciled as exception; approver notified',
      application: 'Registered as application',
      cluster: 'Customer {{document}} created, rule created',
      installment: 'Recurrence created',
      paidBill: 'Bill created as paid',
      fee: 'Fee registered, rule created',
      receipt: 'One-off receipt registered',
      rejected: 'Suggestion rejected. Alchemy will not suggest this link again.',
      ignored: 'Movement ignored',
      restored: 'Movement back in the queue',
      undone_one: 'Reconciliation undone. The movement is back in the queue.',
      undone_other: '{{count}} reconciliations undone. The movements are back in the queue.',
      failed: 'Could not complete the action.',
      unbalanced: 'Adjust the types until the difference closes.',
      selectCandidate: 'Select at least one document to reconcile.',
      created: {
        FEE_BILL: 'Fee created and reconciled',
        VENDOR_ADVANCE_BILL: 'Advance created and reconciled',
        INTERNAL_TRANSFER: 'Transfer created and reconciled',
        INTERNAL_OPERATION: 'Internal operation reconciled',
        HISTORICAL_LEDGER: 'Reconciled as already booked in the imported history',
      },
      openCreated: 'Open',
      reconcileFailed: 'Reconciliation not completed',
      undoneTitle: 'Reconciliation undone',
      undoneBody: 'The movement is back in the queue.',
      batchTitle_one: '{{count}} reconciled',
      batchTitle_other: '{{count}} reconciled',
      batchLast: 'Last: {{name}} {{amount}}',
      dismiss: 'Dismiss',
      /** ALC-4328 round 2 (O8): non-blocking notices on a saved pick. */
      notices: {
        RECON_V2_INTERNAL_OP_RESGATE_RAZAO_NO_CREDIT:
          'The imported Razão did not credit this investment account on {{date}} ({{money}}). The movement balance and the Razão will differ.',
      },
    },
    shortcutsToast:
      'J/K or ↑/↓ navigate · Enter reconciles · R rejects · I ignores · Z undoes · / searches',
    shortcutsToastNoUndo:
      'J/K or ↑/↓ navigate · Enter reconciles · R rejects · I ignores · / searches',
  },
  adjustmentTypes: {
    TARIFA: 'Bank fee',
    JUROS: 'Interest',
    MULTA: 'Penalty',
    DESCONTO: 'Discount',
    IOF: 'IOF',
    IRRF: 'IRRF withheld',
    INSS: 'INSS withheld',
    ISS: 'ISS withheld',
    FX: 'FX variation',
    ARREDONDAMENTO: 'Rounding',
    ADIANTAMENTO: 'Advance',
    DEVOLUCAO_ADIANTAMENTO: 'Advance refund',
    PIS: 'PIS withheld',
    COFINS: 'COFINS withheld',
    CSLL: 'CSLL withheld',
    TARIFA_ADQUIRENCIA: 'Acquirer fee',
    DESCONTO_INCONDICIONAL: 'Unconditional discount (accounting adjustment)',
    CSRF: 'CSRF (PIS + COFINS + CSLL)',
  },
  log: {
    intro:
      'Everything reconciled on this account in {{month}}, grouped by who decided. Rule-made entries can be undone item by item; undoing returns the movement to the queue and reverses the journal.',
    columns: {
      who: 'Who',
      what: 'What',
      items: 'Items',
      amount: 'Amount',
      when: 'When',
    },
    who: {
      RULES: 'Rule',
      HISTORICAL: 'History',
      AI: 'AI',
      AGENT: 'Agent',
      MANUAL: 'You',
    },
    when: {
      ON_IMPORT: 'on import',
      BY_USER: 'by a person',
    },
    volumeSuffix: 'vol.',
    expand: 'Show items',
    collapse: 'Hide items',
    more_one: 'and {{count}} more',
    more_other: 'and {{count}} more',
    undo: 'Undo',
    undoUnavailable:
      'Cannot undo: a journal of this reconciliation cannot be reversed automatically, or an advance or reimbursement it created has already been used.',
    undoBlocked: {
      MOVEMENT_JOURNAL_POSTED:
        'Cannot undo: this yield or investment tax (IR/IOF) is already booked on the bank movement itself. Reverse that journal in the General Ledger first, then undo the reconciliation.',
      GL_POSTING_NOT_REVERSIBLE:
        'Cannot undo: a journal of this reconciliation cannot be reversed automatically. Ask an owner to review its journals.',
      PARTY_BALANCE_CONSUMED:
        'Cannot undo: the advance or reimbursement this reconciliation created has already been used.',
      CREATED_LEG_LINKED_ELSEWHERE:
        'Cannot undo: the movement created on the investment account is now part of another reconciliation. Undo that reconciliation first.',
      CREATED_LEG_PERIOD_CLOSED:
        'Cannot undo: the movement created on the investment account is in a closed month. Reopen the month first.',
      // ALC-4363 r1: only a provider-removed item carries these two; the row names the month.
      STATEMENT_CLOSED:
        'Cannot undo: the statement month of this reconciliation is closed. Reopen the statement first.',
      ACCOUNTING_PERIOD_CLOSED:
        'Cannot undo: the accounting period of the journal entry is closed. Reopen the period first.',
    },
    empty: 'Nothing reconciled yet.',
  },
  undo: {
    progress_one:
      'Reconciliation undone. {{count}} more from the same action: confirm the next one.',
    progress_other:
      'Reconciliation undone. {{count}} more from the same action: confirm the next one.',
    errors: {
      JUSTIFICATION_REQUIRED:
        'Enter a justification: this reconciliation has journals that will be reversed.',
      REVERSAL_DATE_REQUIRED_FOR_BOOK:
        'Pick the reversal period for every book with journals from this reconciliation.',
      REVERSAL_DATE_MISMATCH_FOR_SOURCE:
        'The journals of one movement must be reversed on the same date. Pick the same period for every book.',
      REVERSAL_BEFORE_ORIGINAL_PERIOD:
        'The reversal date cannot be earlier than the original journal. Pick the same or a later period.',
      NO_OPEN_PERIOD:
        'There is no open accounting period on the reversal date. Open it or pick another before undoing.',
      PAYMENT_CLEARING_REVERSAL_JOURNAL_MISSING:
        'The payment clearing reversal was not created in one of the books. Nothing was undone; ask an owner to review this reconciliation’s journals.',
      GROUP_HAS_LIVE_GL_POSTING:
        'This reconciliation still has a live journal that cannot be reversed automatically. Nothing was undone; ask an owner to review its journals.',
      RECONCILIATION_UNDO_MOVEMENT_JOURNAL_POSTED:
        'This yield or investment tax (IR/IOF) is already booked on the bank movement itself, and undo does not reverse that journal. Reverse the yield/tax journal in the General Ledger first, then undo the reconciliation. Nothing was undone.',
      RECONCILIATION_UNDO_CREATED_BILL_NOT_DRAFT:
        'The bill this reconciliation created is no longer a draft (in approval, approved, accrued or paid). Resolve it in Accounts Payable before undoing.',
      GROUP_NOT_FOUND: 'This reconciliation was already undone elsewhere. The list was refreshed.',
      failed: 'The reconciliation could not be undone. Nothing was changed.',
      RECONCILIATION_UNDO_MISMATCH_REMAINDER_IN_USE:
        'The remaining amount this reconciliation scheduled was already sent to the bank or paid. Undo would delete that payment. Nothing was undone. If another bank line reconciled that amount, undo that reconciliation first. If the amount is at the bank, keep this reconciliation.',
      RECONCILIATION_UNDO_MISMATCH_PAYMENT_NOT_RESTORABLE:
        'This reconciliation replaced a payment that was confirmed or held by the bank, or the bill got another payment later. Undo cannot put the bill back as it was. Nothing was undone. If you added a payment later, delete it and try again. If not, keep this reconciliation.',
      RECONCILIATION_UNDO_CREATED_LEG_LINKED_ELSEWHERE:
        'The movement this application/redemption created on the investment account is now part of another reconciliation. Undo that reconciliation first. Nothing was undone.',
      RECONCILIATION_UNDO_INVESTMENT_GL_BELOW_ZERO:
        "Undoing this application would take the investment account's GL balance below zero, because a later redemption or a manual journal entry already took that amount out. Undo the later redemption or reverse the manual entry first. Nothing was undone.",
      RECONCILIATION_UNDO_LATER_RESGATE_POSTED:
        'A later redemption on this investment is still posted. Undo the redemption of {{laterDate}} on this investment first. Nothing was undone.',
      INVESTMENT_REVERSAL_BEFORE_POSTED_RESGATE:
        'This reversal would raise the GL balance the redemption of {{laterDate}} used to split principal and financial income. Undo the redemption of {{laterDate}} on this investment first. Nothing was undone.',
      // ALC-4363 r2 (ORCH Q16): a bank-removed line whose journal month is closed.
      RECONCILIATION_UNDO_PROVIDER_REMOVED_ACCOUNTING_PERIOD_CLOSED:
        'Reopen the {{month}} {{year}} accounting period to undo. The journal entry of this line, which the bank removed, is in that closed period. Nothing was undone.',
    },
  },
  ignored: {
    intro:
      'Ignored movements are excluded from the balance proof and listed in the month close as exceptions.',
    columns: {
      date: 'Date',
      movement: 'Movement',
      reason: 'Reason',
      amount: 'Amount',
    },
    restore: 'Restore',
    empty: 'No ignored movements on this account.',
  },
};
